<template>
    <div
        class="workforce-page"
        :class="{'workforce-page--overview': overviewMode}"
        :style="{
            '--workforce-toolbar-height': `${toolbarHeight}px`
        }"
        @click="closeContextMenu"
        @dragover.capture="onEmployeeDragOver"
    >
        <header class="workforce-page__header">
            <div class='workforce-page__title'>
                <div class='workforce-page__title-row'>
                    <h1>Распределение сотрудников</h1>
                    <collaboration-participants scope="board" :ready="syncStatus === 'connected' && permissions.can_board" />
                    <div class='workforce-sync-state' aria-live='polite'>
                        <span
                            class='workforce-sync-state__connection'
                            :class='`workforce-sync-state__connection--${syncStatus}`'
                        >
                            <i class='fas fa-circle' aria-hidden='true'></i>
                            {{ syncStatusLabel }}
                        </span>
                        <span
                            v-if='remoteChangePending && realtimeInteractionActive'
                            class='workforce-sync-state__pending'
                        >
                            <i class='fas fa-clock' aria-hidden='true'></i>
                            Есть изменения — применим после завершения действия
                        </span>
                        <span
                            v-else-if='syncNotice'
                            class='workforce-sync-state__notice'
                        >
                            <i class='fas fa-check' aria-hidden='true'></i>
                            {{ syncNotice }}
                        </span>
                    </div>
                </div>
                <p>Состав объектов и групп на выбранную дату</p>
            </div>
            <div
                class="workforce-page__space-control"
            >
                <b-button-group size="sm" aria-label="Вид планирования">
                    <b-button variant="primary" active>
                        <i class="fas fa-columns mr-1" aria-hidden="true"></i>
                        Доска
                    </b-button>
                    <b-button
                        variant="outline-primary"
                        :to="{name: 'employees-workforce-plan'}"
                    >
                        <i class="far fa-calendar-alt mr-1" aria-hidden="true"></i>
                        План
                    </b-button>
                </b-button-group>
                <employee-name-format-switcher
                    :value="employeeNameFormat"
                    @input="setEmployeeNameFormat"
                ></employee-name-format-switcher>
                <span class="workforce-create-employee-slot">
                    <b-button
                        v-if="permissions.can_manage_employees"
                        size="sm"
                        variant="success"
                        class="w-100"
                        @click="createEmployeeVisible = true"
                    >
                        <i class="fas fa-user-plus mr-1" aria-hidden="true"></i>
                        Создать сотрудника
                    </b-button>
                </span>
                <span
                    v-if="!isMobileToolbarLayout && !overviewMode && workspacePanelHintVisible"
                    class="workforce-page__space-hint"
                >
                    <i class="far fa-lightbulb" aria-hidden="true"></i>
                    Скрывает поиск и фильтры, область доски и левое меню
                </span>
                <b-button
                    v-if="!isMobileToolbarLayout && !overviewMode"
                    type="button"
                    size="sm"
                    variant="primary"
                    class="workforce-page__space-toggle"
                    :title="workspacePanelsCollapsed
                        ? 'Развернуть поиск и фильтры, область доски и левое меню'
                        : 'Свернуть поиск и фильтры, область доски и левое меню'"
                    :aria-pressed="workspacePanelsCollapsed ? 'true' : 'false'"
                    @click="toggleWorkspacePanels"
                >
                    <i
                        class="fas mr-1"
                        :class="workspacePanelsCollapsed
                            ? 'fa-columns'
                            : 'fa-expand-alt'"
                        aria-hidden="true"
                    ></i>
                    {{ workspacePanelsCollapsed ? 'Показать панели' : 'Больше места' }}
                </b-button>
            </div>
        </header>

        <nav
            v-if="hasLoaded && !accessDenied"
            class="workforce-overview-controls"
            :class="{
                'workforce-overview-controls--overview': overviewMode
            }"
            aria-label="Режим отображения доски"
        >
            <b-button-group
                class="workforce-overview-controls__switch"
                size="sm"
                aria-label="Режим отображения доски"
            >
                <b-button
                    :variant="isCompactDensity ? 'primary' : 'outline-primary'"
                    :aria-pressed="isCompactDensity ? 'true' : 'false'"
                    @click="setBoardDensity('compact')"
                >
                    <i class="fas fa-compress-alt mr-1" aria-hidden="true"></i>
                    Обычный
                </b-button>
                <b-button
                    id="workforce-overview-density-button"
                    :variant="isOverviewDensity ? 'primary' : 'outline-primary'"
                    :aria-pressed="isOverviewDensity ? 'true' : 'false'"
                    @click="setBoardDensity('overview')"
                >
                    <i class="fas fa-table mr-1" aria-hidden="true"></i>
                    Обзор
                </b-button>
            </b-button-group>
            <employee-workforce-search
                v-if="overviewMode"
                ref="overviewSearchInput"
                v-model.trim="filters.query"
                class="workforce-overview-controls__search"
                placeholder="Найти сотрудника"
                aria-label="Поиск сотрудников"
                :result-count="searchResultCount"
                :position-label="searchResultPositionLabel"
                @navigate="navigateSearchResults"
            ></employee-workforce-search>
            <b-form-select
                v-if="overviewMode"
                v-model="filters.citizenship"
                :options="citizenshipOptions"
                size="sm"
                class="workforce-overview-controls__citizenship"
                aria-label="Фильтр по гражданству"
            ></b-form-select>
            <b-form-select
                v-if="overviewMode"
                v-model="filters.hiringForm"
                :options="hiringFormOptions"
                size="sm"
                class="workforce-overview-controls__citizenship"
                aria-label="Фильтр по форме найма"
            ></b-form-select>
            <employee-multi-filter v-if="overviewMode"
                                   :itr.sync="filters.itr"
                                   :skills.sync="filters.skills" :positions.sync="filters.positions"
                                   :skill-options="skillOptions" :position-options="positionOptions"
                                   :skill-descriptions="skillDescriptions"
                                   class="workforce-overview-controls__qualifications" />
            <span
                class="workforce-overview-controls__filters"
                :class="{
                    'workforce-overview-controls__filters--active':
                        collapsedActiveFilterCount > 0
                }"
                :title="`Активных фильтров: ${collapsedActiveFilterCount}`"
            >
                <i class="fas fa-filter" aria-hidden="true"></i>
                {{ collapsedActiveFilterCount }}
            </span>
            <b-button
                type="button"
                size="sm"
                variant="outline-secondary"
                class="workforce-overview-controls__history"
                @click="openWorkforceHistory"
            >
                <i class="fas fa-history mr-1" aria-hidden="true"></i>
                История
            </b-button>
            <b-popover
                target="workforce-overview-density-button"
                :show="overviewHintPopoverVisible"
                placement="bottom"
                triggers="manual"
                custom-class="workforce-overview-hint"
            >
                <template #title>Новый режим «Обзор»</template>
                <p>
                    Все объекты и сотрудники в одной таблице. Прокручивайте
                    доску в стороны; планы показаны пунктиром.
                </p>
                <b-button
                    size="sm"
                    variant="primary"
                    @click="dismissOverviewHint"
                >
                    Понятно
                </b-button>
            </b-popover>
        </nav>

        <b-alert v-if="accessDenied" show variant="warning">
            <i class="fas fa-lock mr-1" aria-hidden="true"></i>
            У вас нет доступа к доске распределения сотрудников.
        </b-alert>

        <template v-else>
        <timesheet-incident-panel v-if="hasLoaded" ref="timesheetIncidents"></timesheet-incident-panel>
            <b-alert
                v-if="hasLoaded && board.plan_mode === 'workforce_plan'"
                show
                variant="info"
                class="workforce-plan-slice-notice"
            >
                <span>
                    <i class="far fa-calendar-alt mr-1" aria-hidden="true"></i>
                    Будущий срез построен по единому плану. Фактическое распределение не меняется.
                </span>
                <b-button
                    v-if="permissions.can_edit_plan === true"
                    size="sm"
                    variant="outline-primary"
                    :to="{
                        name: 'employees-workforce-plan',
                        query: {date: boardDate, plan_intent: 'date'}
                    }"
                >
                    Изменить план
                </b-button>
            </b-alert>
            <section
                v-show="!overviewMode"
                ref="toolbar"
                class="workforce-toolbar"
                :class="{
                    'workforce-toolbar--filtered': hasActiveFilters,
                    'workforce-toolbar--collapsed': toolbarPanelIsCollapsed
                }"
            >
                <div
                    v-if="toolbarPanelIsCollapsed"
                    class="workforce-toolbar__collapsed-strip"
                >
                    <button
                        type="button"
                        title="Развернуть поиск и фильтры"
                        aria-label="Развернуть поиск и фильтры"
                        :aria-expanded="false"
                        @click="toolbarCollapsed = false"
                    >
                        <i class="fas fa-chevron-down" aria-hidden="true"></i>
                        <span>Поиск и фильтры</span>
                    </button>
                    <span
                        class="workforce-toolbar__collapsed-date"
                        title="Дата доски"
                    >
                        <i class="far fa-calendar-alt" aria-hidden="true"></i>
                        {{ formatBoardDate(boardDate) }}
                    </span>
                    <span
                        class="workforce-toolbar__collapsed-filter-count"
                        :class="{
                            'workforce-toolbar__collapsed-filter-count--active':
                                collapsedActiveFilterCount > 0
                        }"
                        :title="`Активных фильтров: ${collapsedActiveFilterCount}`"
                    >
                        <i class="fas fa-filter" aria-hidden="true"></i>
                        {{ collapsedActiveFilterCount }}
                        <span class="sr-only">активных фильтров</span>
                    </span>
                </div>

                <b-button
                    v-if="!isMobileToolbarLayout && !toolbarPanelIsCollapsed"
                    size="sm"
                    variant="outline-secondary"
                    class="workforce-toolbar__desktop-collapse"
                    title="Свернуть поиск и фильтры"
                    aria-label="Свернуть поиск и фильтры"
                    :aria-expanded="true"
                    @click="toolbarCollapsed = true"
                >
                    <i class="fas fa-chevron-up" aria-hidden="true"></i>
                </b-button>

                <div v-show="!toolbarPanelIsCollapsed" class="workforce-toolbar__filters">
                    <div
                        class="workforce-toolbar__date"
                        :class="{
                            'workforce-toolbar__date--filtered': !boardIsToday
                        }"
                    >
                        <label for="workforce-board-date">
                            Доска по состоянию на:
                        </label>
                        <b-form-input
                            id="workforce-board-date"
                            v-model="boardDate"
                            type="date"
                            size="sm"
                            :disabled="boardMutationPending || loading"
                            @change="loadBoard"
                        ></b-form-input>
                        <b-button
                            type="button"
                            size="sm"
                            variant="primary"
                            class="workforce-toolbar__today"
                            :disabled="boardMutationPending || loading"
                            @click="showToday"
                        >
                            <i class="far fa-calendar-check" aria-hidden="true"></i>
                            Сегодня
                        </b-button>
                    </div>

                    <employee-workforce-search
                        ref="employeeSearchInput"
                        v-model.trim="filters.query"
                        class="workforce-toolbar__search"
                        placeholder="Сотрудник, должность или контакт"
                        aria-label="Поиск сотрудников"
                        :result-count="searchResultCount"
                        :position-label="searchResultPositionLabel"
                        @navigate="navigateSearchResults"
                    ></employee-workforce-search>

                    <b-button
                        v-if="isMobileToolbarLayout"
                        type="button"
                        size="sm"
                        variant="outline-secondary"
                        class="workforce-toolbar__mobile-toggle"
                        :aria-expanded="mobileToolbarExpanded ? 'true' : 'false'"
                        @click="toggleMobileToolbar"
                    >
                        <span>
                            <i class="fas fa-sliders-h" aria-hidden="true"></i>
                            Фильтры и действия
                        </span>
                        <span>
                            <b-badge
                                v-if="hiddenActiveFilterCount"
                                variant="primary"
                                pill
                            >
                                {{ hiddenActiveFilterCount }}
                                <span class="sr-only">активных скрытых фильтров</span>
                            </b-badge>
                            <i
                                class="fas"
                                :class="mobileToolbarExpanded
                                    ? 'fa-chevron-up'
                                    : 'fa-chevron-down'"
                                aria-hidden="true"
                            ></i>
                        </span>
                    </b-button>

                    <b-form-select
                        v-show="secondaryToolbarControlsVisible"
                        v-model="filters.departmentId"
                        :options="departmentOptions"
                        size="sm"
                        class="workforce-toolbar__select"
                        aria-label="Фильтр по отделу"
                    ></b-form-select>

                    <b-form-select
                        v-show="secondaryToolbarControlsVisible"
                        v-model="filters.priority"
                        :options="priorityOptions"
                        size="sm"
                        class="workforce-toolbar__select workforce-toolbar__priority"
                        aria-label="Фильтр по приоритету"
                    ></b-form-select>

                    <b-form-select
                        v-show="secondaryToolbarControlsVisible"
                        v-model="filters.citizenship"
                        :options="citizenshipOptions"
                        size="sm"
                        class="workforce-toolbar__select"
                        aria-label="Фильтр по гражданству"
                    ></b-form-select>
                    <b-form-select
                        v-show="secondaryToolbarControlsVisible"
                        v-model="filters.hiringForm"
                        :options="hiringFormOptions"
                        size="sm"
                        class="workforce-toolbar__select"
                        aria-label="Фильтр по форме найма"
                    ></b-form-select>
                    <employee-multi-filter v-show="secondaryToolbarControlsVisible"
                                           :itr.sync="filters.itr"
                                           :skills.sync="filters.skills" :positions.sync="filters.positions"
                                           :skill-options="skillOptions" :position-options="positionOptions"
                                           :skill-descriptions="skillDescriptions" />
                </div>

                <div
                    v-show="secondaryToolbarControlsVisible && !toolbarPanelIsCollapsed"
                    id="workforce-secondary-controls"
                    class="workforce-toolbar__summary"
                >
                    <span title="Сотрудники, назначенные на группы и объекты">
                        <i class="fas fa-user-check" aria-hidden="true"></i>
                        Назначено: <strong>{{ assignedCount }}</strong>
                    </span>
                    <span title="Назначенные сотрудники без временного отсутствия">
                        <i class="fas fa-hard-hat" aria-hidden="true"></i>
                        Присутствует: <strong>{{ presentCount }}</strong>
                    </span>
                    <b-button
                        v-if="permissions.view_all_objects"
                        size="sm"
                        :variant="reserveExpanded ? 'secondary' : 'outline-secondary'"
                        :aria-expanded="reserveExpanded ? 'true' : 'false'"
                        @click="reserveExpanded = !reserveExpanded"
                    >
                        <i class="fas fa-archive mr-1" aria-hidden="true"></i>
                        Резерв: {{ filteredLane('reserve').length }}
                    </b-button>
                    <b-button
                        v-if="permissions.can_registry"
                        size="sm"
                        variant="outline-primary"
                        title="Полный реестр, включая уволенных и сотрудников без учётной записи"
                        @click="openEmployeeRegistry"
                    >
                        <i class="fas fa-address-book mr-1" aria-hidden="true"></i>
                        Все сотрудники
                    </b-button>
                    <b-button
                        v-if="permissions.can_settings"
                        size="sm"
                        variant="outline-secondary"
                        title="Настройки модуля «Сотрудники»"
                        @click="openSettings"
                    >
                        <i class="fas fa-cog mr-1" aria-hidden="true"></i>
                        Настройки
                    </b-button>
                    <b-button
                        size="sm"
                        variant="outline-secondary"
                        :disabled="loading"
                        title="Обновить доску"
                        @click="loadBoard"
                    >
                        <i class="fas fa-sync-alt" :class="{'fa-spin': loading}" aria-hidden="true"></i>
                        <span class="sr-only">Обновить</span>
                    </b-button>
                </div>

                <div
                    v-if="visibleActiveFilterChips.length"
                    class="workforce-active-filters"
                >
                    <span
                        v-if="secondaryToolbarControlsVisible && !toolbarPanelIsCollapsed"
                        class="workforce-active-filters__label"
                    >
                        <i class="fas fa-filter" aria-hidden="true"></i>
                        Включены фильтры:
                    </span>
                    <button
                        v-for="chip in visibleActiveFilterChips"
                        :key="chip.key"
                        type="button"
                        class="workforce-active-filter"
                        :class="`workforce-active-filter--${chip.variant}`"
                        :title="`Снять фильтр: ${chip.text}`"
                        :disabled="
                            chip.key === 'date'
                            && (boardMutationPending || loading)
                        "
                        @click="clearFilterChip(chip.key)"
                    >
                        <i class="fas fa-check" aria-hidden="true"></i>
                        <span class="workforce-active-filter__text">
                            {{ chip.text }}
                        </span>
                        <i
                            class="
                                fas fa-times
                                workforce-active-filter__remove
                            "
                            aria-hidden="true"
                        ></i>
                    </button>
                    <button
                        v-if="secondaryToolbarControlsVisible && !toolbarPanelIsCollapsed"
                        type="button"
                        class="workforce-active-filters__reset"
                        :disabled="boardMutationPending || loading"
                        @click="clearAllFilters"
                    >
                        Сбросить все
                    </button>
                </div>
            </section>

            <b-alert
                v-if="errorMessage"
                show
                dismissible
                variant="danger"
                class="mb-3"
                @dismissed="errorMessage = ''"
            >
                {{ errorMessage }}
            </b-alert>

            <b-alert
                v-if="hasLoaded && !canMove"
                show
                variant="info"
                class="py-2"
            >
                Доска открыта только для просмотра. Для назначения сотрудников необходимо
                право «Перемещение» на всех объектах или на соответствующих объектах.
            </b-alert>

            <div v-if="loading && !hasLoaded" class="workforce-state">
                <b-spinner small variant="primary"></b-spinner>
                <span>Загружаем распределение…</span>
            </div>

            <div
                v-if="hasLoaded && !totalEmployeeCount"
                class="workforce-empty-notice"
            >
                <i class="fas fa-users" aria-hidden="true"></i>
                <div>
                    <strong>На выбранную дату сотрудников нет</strong>
                    <span>
                        Объекты остаются доступны — сотрудников можно назначить прямо в них.
                    </span>
                </div>
            </div>

            <employee-workforce-grid
                v-if="hasLoaded"
                ref="workforceGrid"
                :groups="groups"
                :standalone-objects="standaloneObjects"
                :planned-objects="filteredPlannedObjectLanes"
                :home-employees="filteredLane('home')"
                :distribution-employees="filteredLane('requires_distribution')"
                :rest-employees="filteredLane('rest')"
                :loupe-employees="filteredLane('loupe')"
                :reserve-employees="filteredLane('reserve')"
                :planned-employees-by-target="plannedEmployeesByTarget"
                :filter-employees="filteredEmployees"
                :search-query="filters.query"
                :name-format="employeeNameFormat"
                :object-id="objectId"
                :movement-allowed="movementAllowed"
                :reserve-movement-allowed="reserveMovementAllowed"
                :can-move-all-objects="canMoveAllObjects"
                :mobile="isMobileLayout"
                :compact="isCompactDensity"
                :overview="overviewMode"
                :reserve-expanded="reserveExpanded"
                :dragging-employee-id="draggingEmployeeId"
                :dragging-assignment-id="draggingAssignmentId"
                :planned-move-pending="plannedMovePending"
                :scope-reset-token="boardScopeResetToken"
                :scope-panel-collapsed="scopePanelCollapsed"
                @reserve-expanded="reserveExpanded = $event"
                @scope-panel-collapsed="setScopePanelCollapsed"
                @scope-change="onBoardScopeChange"
                @open="openInspector"
                @move="openAssignmentManager"
                @assign-to-target="openAssignmentManagerForTarget"
                @contextmenu="openContextMenu"
                @dragstart="startDragging"
                @dragend="stopDragging"
                @drop="dropEmployee"
                @confirm-planned-move="confirmPlannedMove"
            ></employee-workforce-grid>
        </template>

        <employee-inspector
            :manage-history="false"
            :employee="assignmentManagerVisible ? null : selectedEmployee"
            :loading="inspectorLoading"
            :can-view-plan="permissions.can_view_plan === true"
            :board-date="boardDate"
            :can-move="canMoveSelectedEmployee"
            :can-manage-characteristics="selectedCardCanManage('characteristics')"
            :can-manage-absences="selectedCardCanManage('absences')"
            :planned-move-pending="plannedMovePending"
            :behind-modal="plannedMoveConfirmationVisible"
            @close="closeInspector"
            @move="openAssignmentManager"
            @confirm-planned-move="confirmPlannedMove"
            @cancel-planned-move="cancelPlannedMove"
            @characteristic-pin-changed="loadBoard"
            @characteristic-added="onInspectorCharacteristicAdded"
            @characteristic-updated="onInspectorCharacteristicAdded"
            @absence-added="onInspectorAbsenceAdded"
            @reserve-returned="onInspectorReserveReturned"
        ></employee-inspector>

        <employee-create-modal
            v-model="createEmployeeVisible"
            @created="onEmployeeCreated"
        ></employee-create-modal>

        <employee-workforce-history
            :visible="historyPanelVisible"
            :object-options="historyObjectOptions"
            :refresh-token="historyRefreshToken"
            @close="closeWorkforceHistory"
            @open-employee="openEmployeeFromHistory"
        ></employee-workforce-history>

        <div
            v-if="contextMenu.visible"
            class="workforce-context-menu"
            :style="{left: `${contextMenu.x}px`, top: `${contextMenu.y}px`}"
            @click.stop
        >
            <button type="button" @click="openInspectorFromContext">
                <i class="fas fa-address-card" aria-hidden="true"></i>
                Сведения
            </button>
            <button
                v-if="canMoveEmployee(contextMenu.employee)"
                type="button"
                @click="moveFromContext"
            >
                <i class="fas fa-map-marked-alt" aria-hidden="true"></i>
                Назначения…
            </button>
            <button
                v-if="canOpenEmployeeCard(contextMenu.employee)"
                type="button"
                @click="openCardFromContext"
            >
                <i class="fas fa-external-link-alt" aria-hidden="true"></i>
                Карточка сотрудника
            </button>
        </div>

        <employee-reserve-return :visible="reserveReturnVisible" :employee-id="reserveReturnEmployeeId" :initial-date="reserveReturnDate" @close="reserveReturnVisible = false" @returned="reserveReturned" />
        <employee-assignment-manager
            :visible="assignmentManagerVisible"
            :action-error="assignmentActionError"
            :employee="assignmentManagerEmployee"
            :employee-options="assignableEmployees"
            :unassigned-employee-options="unassignedEmployees"
            :destination-options="destinationOptions"
            :initial-target="assignmentManagerTarget"
            :board-date="boardDate"
            :future-date="isFutureBoardDate"
            :pending="boardMutationPending || loading"
            show-employee-card-action
            @close="closeAssignmentManager"
            @open-card="openEmployeeCardFromAssignmentManager"
            @move-primary="movePrimaryFromManager"
            @changed="assignmentChanged"
            :external-change-pending='remoteChangePending'
            @editing-state='setAssignmentManagerDirty'
        ></employee-assignment-manager>

        <div v-if="undoState" class="workforce-undo" role="status">
            <div>
                <strong>Сотрудник перемещён</strong>
                <span>Отмена доступна ещё {{ undoSeconds }} сек.</span>
            </div>
            <button type="button" :disabled="undoPending" @click="undoMovement">
                <b-spinner v-if="undoPending" small></b-spinner>
                <span v-else>Отменить</span>
            </button>
        </div>
    </div>
</template>

<script>
import EmployeeMultiFilter from './EmployeeMultiFilter.vue';
import {employeeFilterOptions, matchesEmployeeFilters, employeeSkillDescriptions} from './employeeRegistryFilters';
import {hiringFormOptions, hiringFormLabel, matchesHiringForm} from './employeeHiringForm';
import api from '@/utils/api';
import {requestDesktopSidebarCollapsed} from '@/utils/modernNavigationState';
import {socket} from '@/plugins/socket';
import EmployeeWorkforceGrid from './EmployeeWorkforceGrid.vue';
import EmployeeCreateModal from './EmployeeCreateModal.vue';
import EmployeeInspector from './EmployeeInspector.vue';
import EmployeeAssignmentManager from './EmployeeAssignmentManager.vue';
import EmployeeReserveReturn from './EmployeeReserveReturn.vue';
import EmployeeWorkforceHistory from './EmployeeWorkforceHistory.vue';
import CollaborationParticipants from '@/components/CollaborationParticipants.vue';
import EmployeeWorkforceSearch from './EmployeeWorkforceSearch.vue';
import EmployeeNameFormatSwitcher from './EmployeeNameFormatSwitcher.vue';
import TimesheetIncidentPanel from './TimesheetIncidentPanel.vue';
import employeeNameFormat from '@/utils/employeeNameFormat';

const INSPECTOR_HISTORY_STATE_KEY = 'workforceEmployeeInspector';
const WORKFORCE_HISTORY_PANEL_STATE_KEY = 'workforceHistoryPanel';
const WORKFORCE_RETURN_HISTORY_STATE_KEY = 'employeeWorkforceReturnView';
const OVERVIEW_LANDSCAPE_BODY_CLASS =
    'employee-workforce-overview-landscape';

function currentLocalDate() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function emptyBoard(date) {
    return {
        date,
        permissions: {},
        settings: {},
        groups: [],
        standalone_objects: [],
        planned_objects: [],
        lanes: {
            home: [],
            requires_distribution: [],
            rest: [],
            loupe: [],
            reserve: []
        },
        departments: []
    };
}

export default {
    name: 'EmployeeWorkforceBoard',
    components: {
        EmployeeReserveReturn,
        CollaborationParticipants,
        EmployeeMultiFilter,
        EmployeeWorkforceGrid,
        EmployeeCreateModal,
        EmployeeInspector,
        EmployeeAssignmentManager,
        EmployeeWorkforceHistory,
        EmployeeWorkforceSearch,
        EmployeeNameFormatSwitcher,
        TimesheetIncidentPanel
    },
    data() {
        const routeDate = this.$route && this.$route.query
            ? String(this.$route.query.date || '')
            : '';
        const date = /^\d{4}-\d{2}-\d{2}$/.test(routeDate)
            ? routeDate
            : currentLocalDate();
        return {
            boardDate: date,
            board: emptyBoard(date),
            boardRequestId: 0,
            loading: false,
            hasLoaded: false,
            accessDenied: false,
            errorMessage: '',
            createEmployeeVisible: false,
            employeeNameFormat: employeeNameFormat.readEmployeeNameFormat(),
            filters: {
                query: '',
                departmentId: '',
                priority: '',
                hiringForm: '',
                skills: [],
                itr: false,
                positions: [],
                citizenship: ''
            },
            viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1280,
            viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 720,
            toolbarHeight: 0,
            toolbarResizeObserver: null,
            draggingEmployee: null,
            draggingEmployeeId: null,
            draggingAssignmentId: null,
            dragAutoScrollFrame: null,
            dragAutoScrollSpeed: 0,
            selectedEmployee: null,
            inspectorLoading: false,
            inspectorRequestId: 0,
            inspectorHistoryToken: null,
            historyPanelVisible: false,
            historyPanelToken: null,
            historyRefreshToken: 0,
            contextMenu: {
                visible: false,
                x: 0,
                y: 0,
                employee: null
            },
            reserveReturnVisible: false,
            reserveReturnEmployeeId: null,
            reserveReturnDate: '',
            assignmentActionError: '',
            assignmentManagerVisible: false,
            assignmentManagerEmployee: null,
            assignmentManagerTarget: null,
            movementPending: false,
            plannedMovePending: false,
            plannedMoveConfirmationVisible: false,
            undoState: null,
            undoSeconds: 0,
            undoTimer: null,
            undoPending: false,
            reserveExpanded: false,
            boardDensity: 'compact',
            overviewHintDismissed: false,
            boardScopeFilter: null,
            boardScopeResetToken: 0,
            searchResultCount: 0,
            searchResultIndex: -1,
            mobileToolbarExpanded: false,
            mobileToolbarAutoCloseReady: false,
            mobileToolbarAutoCloseTimer: null,
            toolbarCollapsed: false,
            scopePanelCollapsed: false,
            workspacePanelStateReady: false,
            workspacePanelHintVisible: true,
            syncStatus: 'connecting',
            syncNotice: '',
            remoteChangePending: false,
            assignmentManagerDirty: false,
            realtimeRefreshTimer: null,
            realtimePollingTimer: null,
            realtimeNoticeTimer: null,
            realtimeHighlightTimer: null,
            movementHighlightStartTimer: null,
            movementHighlightTimer: null,
            realtimeLastRevision: 0,
            pendingReturnViewState: null
        };
    },
    computed: {
        skillOptions() { return employeeFilterOptions(this.allEmployees, 'skills'); },
        positionOptions() { return employeeFilterOptions(this.allEmployees, 'positions'); },
        skillDescriptions() { return employeeSkillDescriptions(this.allEmployees); },
        hiringFormOptions() { return hiringFormOptions; },
        groups() {
            return Array.isArray(this.board.groups) ? this.board.groups : [];
        },
        standaloneObjects() {
            return Array.isArray(this.board.standalone_objects)
                ? this.board.standalone_objects
                : [];
        },
        plannedObjectLanes() {
            return (Array.isArray(this.board.planned_objects)
                ? this.board.planned_objects
                : []
            ).filter(item => Array.isArray(item.employees) && item.employees.length);
        },
        filteredPlannedObjectLanes() {
            return this.plannedObjectLanes.map(item => Object.assign({}, item, {
                employees: this.filteredEmployees(item.employees)
            })).filter(item => item.employees.length);
        },
        historyObjectOptions() {
            const options = [];
            const seen = new Set();
            const addObject = function (object, groupName) {
                const id = Number(object && (object.id || object.building_id));
                if (!id || seen.has(id)) return;
                seen.add(id);
                const name = object.name || `Объект #${id}`;
                options.push({
                    value: String(id),
                    text: groupName ? `${groupName} — ${name}` : name
                });
            };
            this.groups.forEach(function (group) {
                (Array.isArray(group.objects) ? group.objects : [])
                    .forEach(function (object) {
                        addObject(object, group.name);
                    });
            });
            this.standaloneObjects.forEach(function (object) {
                addObject(object, 'Без группы');
            });
            return options.sort(function (left, right) {
                return left.text.localeCompare(right.text, 'ru');
            });
        },
        lanes() {
            return this.board.lanes || {};
        },
        plannedEmployeesByTarget() {
            if (this.board && this.board.plan_mode === 'workforce_plan') {
                return {};
            }
            const result = {};
            this.allEmployees.forEach(employee => {
                const moves = employee && Array.isArray(employee.planned_moves)
                    ? employee.planned_moves
                    : (employee && employee.planned_move ? [employee.planned_move] : []);
                moves.forEach(move => {
                    if (
                        !move ||
                        move.status !== 'planned' ||
                        (
                            move.target_type !== 'object' &&
                            move.target_type !== 'object_group'
                        ) ||
                        move.target_id === null ||
                        move.target_id === undefined
                    ) {
                        return;
                    }
                    const key = `${move.target_type}:${move.target_id}`;
                    if (!result[key]) {
                        result[key] = [];
                    }
                    result[key].push(Object.assign({}, employee, {
                        planned_move: move,
                        planned_moves: [move],
                        can_confirm_planned_move: this.canConfirmPlannedMoveAtTarget(move),
                        workforce_planned_projection: true
                    }));
                });
            });
            return result;
        },
        permissions() {
            return this.board.permissions || {};
        },
        usesCoarsePointer() {
            return typeof window !== 'undefined' &&
                typeof window.matchMedia === 'function' &&
                window.matchMedia('(pointer: coarse)').matches;
        },
        isMobileLayout() {
            return Boolean(this.$store.state.isMobile) ||
                this.viewportWidth < 768 ||
                (
                    this.usesCoarsePointer &&
                    Math.min(this.viewportWidth, this.viewportHeight) < 768
                );
        },
        isMobileToolbarLayout() {
            return this.viewportWidth < 768;
        },
        secondaryToolbarControlsVisible() {
            return !this.isMobileToolbarLayout || this.mobileToolbarExpanded;
        },
        toolbarPanelIsCollapsed() {
            return !this.isMobileToolbarLayout && this.toolbarCollapsed;
        },
        currentUser() {
            return this.$store &&
                this.$store.getters &&
                this.$store.getters.currentUser || null;
        },
        workspacePanelStorageKey() {
            const user = this.currentUser || {};
            const company = user.company || 'company';
            const userId = user.userId || user.user_id || 'anonymous';
            return `employee-workforce:panel-state:v1:${company}:${userId}`;
        },
        boardDensityStorageKey() {
            const user = this.currentUser || {};
            const company = user.company || 'company';
            const userId = user.userId || user.user_id || 'anonymous';
            return `employee-workforce:density:v2:${company}:${userId}`;
        },
        overviewHintStorageKey() {
            const user = this.currentUser || {};
            const company = user.company || 'company';
            const userId = user.userId || user.user_id || 'anonymous';
            return `employee-workforce:overview-hint:v1:${company}:${userId}`;
        },
        workspacePanelsCollapsed() {
            return this.toolbarCollapsed && this.scopePanelCollapsed;
        },
        isCompactDensity() {
            return this.boardDensity === 'compact';
        },
        isOverviewDensity() {
            return this.boardDensity === 'overview';
        },
        overviewMode() {
            return this.isOverviewDensity;
        },
        mobileOverviewLandscape() {
            return this.hasLoaded &&
                !this.accessDenied &&
                this.overviewMode &&
                this.isMobileLayout &&
                this.viewportWidth > this.viewportHeight;
        },
        overviewHintPopoverVisible() {
            return this.hasLoaded &&
                !this.overviewHintDismissed &&
                !this.overviewMode;
        },
        boardIsToday() {
            return this.boardDate === currentLocalDate();
        },
        searchActive() {
            return Boolean(String(this.filters.query || '').trim());
        },
        searchResultPositionLabel() {
            const position = this.searchResultCount && this.searchResultIndex >= 0
                ? this.searchResultIndex + 1
                : 0;
            return `${position}/${this.searchResultCount}`;
        },
        hasActiveFilters() {
            return this.activeFilterChips.length > 0;
        },
        hiddenActiveFilterCount() {
            return this.hiddenActiveFilterChips.length;
        },
        hiddenActiveFilterChips() {
            return this.activeFilterChips.filter(chip => (
                ['department', 'priority', 'citizenship', 'hiringForm', 'skills', 'positions', 'itr', 'scope']
                    .indexOf(chip.key) !== -1
            ));
        },
        collapsedActiveFilterChips() {
            return this.activeFilterChips.filter(chip => chip.key !== 'date');
        },
        collapsedActiveFilterCount() {
            return this.collapsedActiveFilterChips.length;
        },
        visibleActiveFilterChips() {
            if (this.toolbarPanelIsCollapsed) {
                return this.collapsedActiveFilterChips;
            }
            return this.secondaryToolbarControlsVisible
                ? this.activeFilterChips
                : this.hiddenActiveFilterChips;
        },
        activeDepartmentOption() {
            return this.departmentOptions.find(option => (
                String(option.value) === String(this.filters.departmentId)
            )) || null;
        },
        activePriorityOption() {
            return this.priorityOptions.find(option => (
                String(option.value) === String(this.filters.priority)
            )) || null;
        },
        activeCitizenshipOption() {
            return this.citizenshipOptions.find(option => (
                String(option.value) === String(this.filters.citizenship)
            )) || null;
        },
        activeFilterChips() {
            const chips = [];
            if (this.filters.skills.length) chips.push({key: 'skills', text: 'Навыки: ' + this.filters.skills.join(', '), variant: 'info'});
            if (this.filters.itr) chips.push({key: 'itr', text: 'ИТР', variant: 'info'});
            if (this.filters.positions.length) chips.push({key: 'positions', text: 'Должности: ' + this.filters.positions.join(', '), variant: 'info'});
            if (this.filters.hiringForm) chips.push({key: 'hiringForm', text: 'Форма найма: ' + hiringFormLabel(this.filters.hiringForm), variant: 'secondary'});
            if (!this.boardIsToday) {
                chips.push({
                    key: 'date',
                    text: `Дата: ${this.formatBoardDate(this.boardDate)}`,
                    variant: 'warning'
                });
            }
            const query = String(this.filters.query || '').trim();
            if (query) {
                chips.push({
                    key: 'search',
                    text: `Поиск: ${query}`,
                    variant: 'primary'
                });
            }
            if (this.filters.departmentId && this.activeDepartmentOption) {
                chips.push({
                    key: 'department',
                    text: `Отдел: ${this.activeDepartmentOption.text}`,
                    variant: 'info'
                });
            }
            if (this.filters.priority && this.activePriorityOption) {
                chips.push({
                    key: 'priority',
                    text: this.activePriorityOption.text,
                    variant: 'secondary'
                });
            }
            if (this.filters.citizenship && this.activeCitizenshipOption) {
                chips.push({
                    key: 'citizenship',
                    text: this.activeCitizenshipOption.text,
                    variant: 'warning'
                });
            }
            if (this.boardScopeFilter) {
                chips.push({
                    key: 'scope',
                    text: this.boardScopeFilter.text,
                    variant: 'success'
                });
            }
            return chips;
        },
        selectedDateIsValid() {
            return /^\d{4}-\d{2}-\d{2}$/.test(String(this.boardDate || ''));
        },
        boardMatchesSelectedDate() {
            return this.selectedDateIsValid &&
                String(this.board && this.board.date || '') === String(this.boardDate);
        },
        boardMutationPending() {
            return this.movementPending ||
                this.plannedMovePending ||
                this.undoPending;
        },
        realtimeInteractionActive() {
            return Boolean(
                this.draggingEmployee ||
                this.boardMutationPending ||
                (
                    this.assignmentManagerVisible &&
                    this.assignmentManagerDirty
                )
            );
        },
        syncStatusLabel() {
            if (this.syncStatus === 'connected') return 'Синхронизация включена';
            if (this.syncStatus === 'offline') return 'Нет сети';
            if (this.syncStatus === 'connecting') return 'Подключение…';
            return 'Переподключение…';
        },
        canMove() {
            return this.permissions.can_move === true ||
                this.permissions.move === true ||
                this.permissions.canMove === true;
        },
        canMoveAllObjects() {
            return this.permissions.move_all_objects === true;
        },
        movementAllowed() {
            return this.canMove
                && this.boardMatchesSelectedDate
                && !this.isFutureBoardDate
                && !this.loading
                && !this.boardMutationPending;
        },
        reserveMovementAllowed() {
            return this.movementAllowed
                && this.boardIsToday
                && this.permissions.view_all_objects === true
                && this.permissions.can_manage_employees === true;
        },
        canMoveSelectedEmployee() {
            return this.canMoveEmployee(this.selectedEmployee);
        },
        allEmployees() {
            const employees = [];
            this.pushEmployees(employees, this.lanes.home);
            this.pushEmployees(employees, this.lanes.requires_distribution);
            this.pushEmployees(employees, this.lanes.rest);
            this.pushEmployees(employees, this.lanes.loupe);
            this.pushEmployees(employees, this.lanes.reserve);
            this.groups.forEach(group => {
                this.pushEmployees(employees, group.employees);
                (Array.isArray(group.objects) ? group.objects : []).forEach(object => {
                    this.pushEmployees(employees, object.employees);
                });
            });
            this.standaloneObjects.forEach(object => {
                this.pushEmployees(employees, object.employees);
            });
            this.plannedObjectLanes.forEach(object => {
                this.pushEmployees(employees, object.employees);
            });
            return this.uniqueEmployees(employees);
        },
        assignableEmployees() {
            const source = Array.isArray(this.board.assignable_employees)
                ? this.uniqueEmployees(this.board.assignable_employees)
                : this.allEmployees;
            return source.filter(employee => (
                employee.lifecycle_status !== 'reserve'
            )).sort((left, right) => (
                this.employeeName(left).localeCompare(this.employeeName(right), 'ru')
            ));
        },
        unassignedEmployees() {
            const employees = [];
            this.pushEmployees(employees, this.lanes.home);
            this.pushEmployees(employees, this.lanes.requires_distribution);
            return this.uniqueEmployees(employees)
                .filter(employee => employee.lifecycle_status !== 'reserve')
                .sort((left, right) => (
                    this.employeeName(left).localeCompare(
                        this.employeeName(right),
                        'ru'
                    )
                ));
        },
        totalEmployeeCount() {
            return this.allEmployees.length;
        },
        assignedEmployees() {
            const employees = [];
            this.groups.forEach(group => {
                this.pushEmployees(employees, group.employees);
                (Array.isArray(group.objects) ? group.objects : []).forEach(object => {
                    this.pushEmployees(employees, object.employees);
                });
            });
            this.standaloneObjects.forEach(object => {
                this.pushEmployees(employees, object.employees);
            });
            this.plannedObjectLanes.forEach(object => {
                this.pushEmployees(employees, object.employees);
            });
            return this.uniqueEmployees(employees).filter(this.employeeMatchesFilters);
        },
        assignedCount() {
            return this.assignedEmployees.length;
        },
        presentCount() {
            return this.assignedEmployees.filter(employee => !employee.is_absent).length;
        },
        departmentOptions() {
            const options = [{value: '', text: 'Все отделы'}];
            const departments = Array.isArray(this.board.departments)
                ? this.board.departments
                : [];
            departments.forEach(department => {
                const value = department.department_id || department.id || department.value;
                const text = department.name || department.label || department.text;
                if (value !== null && value !== undefined && text) {
                    options.push({value: String(value), text});
                }
            });
            return options;
        },
        priorityOptions() {
            const values = {};
            this.allEmployees.forEach(employee => {
                if (employee.priority !== null &&
                    employee.priority !== undefined &&
                    employee.priority !== '') {
                    values[String(employee.priority)] = employee.priority;
                }
            });
            const priorities = Object.keys(values).sort((left, right) => {
                const leftNumber = Number(left);
                const rightNumber = Number(right);
                if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
                    return leftNumber - rightNumber;
                }
                return left.localeCompare(right, 'ru');
            });
            return [
                {value: '', text: 'Все приоритеты'},
                {value: '__none__', text: 'Без приоритета'}
            ].concat(priorities.map(value => ({
                value,
                text: `Приоритет ${value}`
            })));
        },
        citizenshipOptions() {
            const values = Array.from(new Set(this.allEmployees.map(employee => (
                String(employee.citizenship || '').trim()
            )).filter(Boolean))).sort((left, right) => left.localeCompare(right, 'ru'));
            return [
                {value: '', text: 'Любое гражданство'},
                {value: '__none__', text: 'Гражданство не указано'}
            ].concat(values.map(value => ({value, text: value})));
        },
        destinationOptions() {
            const options = [{value: '', text: 'Выберите место'}];
            if (!this.canMove) {
                return options;
            }
            if (this.canMoveAllObjects) {
                options.push(
                    {
                        value: 'requires_distribution:',
                        text: 'Не распределены / выбыли из планирования'
                    },
                    {value: 'home:', text: 'Дома / отдых'}
                );
            }
            this.groups.forEach(group => {
                if (group.can_move === true) {
                    options.push({
                        value: `object_group:${group.id}`,
                        text: `${group.name} — на группу`
                    });
                }
                (Array.isArray(group.objects) ? group.objects : []).forEach(object => {
                    if (object.can_move === true) {
                        options.push({
                            value: `object:${this.objectId(object)}`,
                            text: `${group.name} — ${object.name}`
                        });
                    }
                });
            });
            this.standaloneObjects.forEach(object => {
                if (object.can_move === true) {
                    options.push({
                        value: `object:${this.objectId(object)}`,
                        text: `Объект — ${object.name}`
                    });
                }
            });
            return options;
        },
        isFutureBoardDate() {
            return Boolean(this.boardDate && this.boardDate > currentLocalDate());
        }
    },
    watch: {
        'filters.query'() {
            this.scheduleSearchResultsRefresh(true);
        },
        'filters.departmentId'() {
            this.scheduleSearchResultsRefresh(true);
        },
        'filters.priority'() {
            this.scheduleSearchResultsRefresh(true);
        },
        'filters.hiringForm'() { this.scheduleSearchResultsRefresh(true); },
        'filters.skills'() { this.scheduleSearchResultsRefresh(true); },
        'filters.itr'() { this.scheduleSearchResultsRefresh(true); },
        'filters.positions'() { this.scheduleSearchResultsRefresh(true); },
        'filters.citizenship'() {
            this.scheduleSearchResultsRefresh(true);
        },
        reserveExpanded() {
            this.scheduleSearchResultsRefresh(true);
        },
        boardDensity() {
            this.persistBoardDisplayPreferences();
            this.scheduleSearchResultsRefresh(false);
        },
        mobileOverviewLandscape: {
            immediate: true,
            handler() {
                this.syncOverviewLandscapeShell();
            }
        },
        toolbarCollapsed() {
            this.persistWorkspacePanelState();
        },
        scopePanelCollapsed() {
            this.persistWorkspacePanelState();
        }
    },
    async mounted() {
        this.pendingReturnViewState = this.workforceReturnHistoryState();
        this.restoreWorkspacePanelState();
        this.restoreBoardDisplayPreferences();
        this.applyPendingReturnPreferences();
        window.addEventListener('resize', this.onResize);
        window.addEventListener('popstate', this.onInspectorHistoryPopState);
        document.addEventListener('keydown', this.onKeydown);
        window.addEventListener('scroll', this.onBoardScroll, true);
        this.observeToolbar();
        await this.initialize();
        this.setupRealtimeSync();
        this.$nextTick(() => {
            this.measureToolbar();
            this.restorePendingReturnView();
        });
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('popstate', this.onInspectorHistoryPopState);
        document.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('scroll', this.onBoardScroll, true);
        if (this.toolbarResizeObserver) {
            this.toolbarResizeObserver.disconnect();
            this.toolbarResizeObserver = null;
        }
        if (this.mobileToolbarAutoCloseTimer) {
            window.clearTimeout(this.mobileToolbarAutoCloseTimer);
            this.mobileToolbarAutoCloseTimer = null;
        }
        this.stopEmployeeDragAutoScroll();
        this.teardownRealtimeSync();
        this.clearUndoState();
        this.clearOverviewLandscapeShell();
    },
    methods: {
        setEmployeeNameFormat(value) {
            this.employeeNameFormat = employeeNameFormat.writeEmployeeNameFormat(value);
        },
        browserStorage() {
            return typeof window !== 'undefined' ? window.localStorage : null;
        },
        workforceReturnHistoryState() {
            if (typeof window === 'undefined' || !window.history) return null;
            const value = window.history.state &&
                window.history.state[WORKFORCE_RETURN_HISTORY_STATE_KEY];
            return value && value.version === 1 ? value : null;
        },
        captureWorkforceReturnView() {
            const grid = this.$refs.workforceGrid;
            return {
                version: 1,
                boardDate: this.boardDate,
                boardDensity: this.boardDensity,
                filters: Object.assign({}, this.filters),
                toolbarCollapsed: this.toolbarCollapsed,
                scopePanelCollapsed: this.scopePanelCollapsed,
                reserveExpanded: this.reserveExpanded,
                windowScrollX: typeof window !== 'undefined'
                    ? window.pageXOffset
                    : 0,
                windowScrollY: typeof window !== 'undefined'
                    ? window.pageYOffset
                    : 0,
                grid: grid && typeof grid.captureViewState === 'function'
                    ? grid.captureViewState()
                    : null
            };
        },
        writeWorkforceReturnHistoryState(viewState) {
            if (
                typeof window === 'undefined' ||
                !window.history ||
                typeof window.history.replaceState !== 'function'
            ) {
                return;
            }
            const historyState = Object.assign({}, window.history.state || {}, {
                [WORKFORCE_RETURN_HISTORY_STATE_KEY]: viewState
            });
            window.history.replaceState(
                historyState,
                document.title,
                window.location.href
            );
        },
        clearWorkforceReturnHistoryState() {
            if (
                typeof window === 'undefined' ||
                !window.history ||
                typeof window.history.replaceState !== 'function'
            ) {
                return;
            }
            const historyState = Object.assign({}, window.history.state || {});
            delete historyState[WORKFORCE_RETURN_HISTORY_STATE_KEY];
            window.history.replaceState(
                historyState,
                document.title,
                window.location.href
            );
        },
        applyPendingReturnPreferences() {
            const state = this.pendingReturnViewState;
            if (!state) return;
            if (/^\d{4}-\d{2}-\d{2}$/.test(String(state.boardDate || ''))) {
                this.boardDate = state.boardDate;
            }
            this.boardDensity = state.boardDensity === 'overview'
                ? 'overview'
                : 'compact';
            this.filters = Object.assign(
                {},
                this.filters,
                state.filters && typeof state.filters === 'object'
                    ? state.filters
                    : {}
            );
            this.toolbarCollapsed = state.toolbarCollapsed === true;
            this.scopePanelCollapsed = state.scopePanelCollapsed === true;
            this.reserveExpanded = state.reserveExpanded === true;
        },
        restorePendingReturnView() {
            const state = this.pendingReturnViewState;
            if (!state) return;
            const grid = this.$refs.workforceGrid;
            if (grid && typeof grid.restoreViewState === 'function') {
                grid.restoreViewState(state.grid);
            }
            if (typeof window !== 'undefined') {
                window.scrollTo(
                    Number(state.windowScrollX) || 0,
                    Number(state.windowScrollY) || 0
                );
            }
            this.pendingReturnViewState = null;
            this.clearWorkforceReturnHistoryState();
        },
        setupRealtimeSync() {
            socket.on('connect', this.onRealtimeConnect);
            socket.on('disconnect', this.onRealtimeDisconnect);
            socket.on('connect_error', this.onRealtimeConnectError);
            socket.on('workforce:changed', this.onWorkforceChanged);
            window.addEventListener('online', this.onBrowserOnline);
            window.addEventListener('offline', this.onBrowserOffline);
            window.addEventListener('focus', this.onBrowserFocus);
            document.addEventListener(
                'visibilitychange',
                this.onVisibilityChange
            );
            if (typeof navigator !== 'undefined' && navigator.onLine === false) {
                this.syncStatus = 'offline';
                this.startRealtimePolling();
                return;
            }
            this.syncStatus = socket.connected ? 'connecting' : 'reconnecting';
            if (socket.connected) {
                this.subscribeWorkforceSocket();
            } else {
                socket.connect();
            }
        },
        teardownRealtimeSync() {
            socket.off('connect', this.onRealtimeConnect);
            socket.off('disconnect', this.onRealtimeDisconnect);
            socket.off('connect_error', this.onRealtimeConnectError);
            socket.off('workforce:changed', this.onWorkforceChanged);
            if (socket.connected) socket.emit('workforce:unsubscribe');
            window.removeEventListener('online', this.onBrowserOnline);
            window.removeEventListener('offline', this.onBrowserOffline);
            window.removeEventListener('focus', this.onBrowserFocus);
            document.removeEventListener(
                'visibilitychange',
                this.onVisibilityChange
            );
            this.stopRealtimePolling();
            [
                'realtimeRefreshTimer',
                'realtimeNoticeTimer',
                'realtimeHighlightTimer',
                'movementHighlightStartTimer',
                'movementHighlightTimer'
            ].forEach(field => {
                if (this[field]) window.clearTimeout(this[field]);
                this[field] = null;
            });
        },
        subscribeWorkforceSocket() {
            const storage = this.browserStorage();
            const token = storage ? storage.getItem('token') : null;
            if (!socket.connected || !token) {
                this.syncStatus = 'offline';
                this.startRealtimePolling();
                return;
            }
            this.syncStatus = 'connecting';
            this.startRealtimePolling();
            socket.emit('workforce:subscribe', {token}, response => {
                if (response && response.ok === true) {
                    this.syncStatus = 'connected';
                    this.stopRealtimePolling();
                    this.scheduleRealtimeRefresh(null, true);
                    return;
                }
                this.syncStatus = 'reconnecting';
                this.startRealtimePolling();
            });
        },
        onRealtimeConnect() {
            this.subscribeWorkforceSocket();
        },
        onRealtimeDisconnect() {
            this.syncStatus = typeof navigator !== 'undefined' &&
                navigator.onLine === false
                ? 'offline'
                : 'reconnecting';
            this.startRealtimePolling();
        },
        onRealtimeConnectError() {
            this.onRealtimeDisconnect();
        },
        onBrowserOnline() {
            this.syncStatus = 'reconnecting';
            this.startRealtimePolling();
            if (socket.connected) {
                this.subscribeWorkforceSocket();
            } else {
                socket.connect();
            }
            this.scheduleRealtimeRefresh(null, true);
        },
        onBrowserOffline() {
            this.syncStatus = 'offline';
            this.startRealtimePolling();
        },
        onBrowserFocus() {
            if (socket.connected && this.syncStatus !== 'connected') {
                this.subscribeWorkforceSocket();
            }
            this.scheduleRealtimeRefresh(null, true);
        },
        onVisibilityChange() {
            if (document.visibilityState === 'visible') {
                this.scheduleRealtimeRefresh(null, true);
            }
        },
        startRealtimePolling() {
            if (this.realtimePollingTimer) return;
            this.realtimePollingTimer = window.setInterval(() => {
                if (
                    document.visibilityState === 'visible' &&
                    (typeof navigator === 'undefined' || navigator.onLine !== false)
                ) {
                    if (socket.connected && this.syncStatus !== 'connected') {
                        this.subscribeWorkforceSocket();
                    }
                    this.scheduleRealtimeRefresh(null, true);
                }
            }, 10000);
        },
        stopRealtimePolling() {
            if (!this.realtimePollingTimer) return;
            window.clearInterval(this.realtimePollingTimer);
            this.realtimePollingTimer = null;
        },
        onWorkforceChanged(event) {
            const revision = Number(event && event.revision || 0);
            if (revision && revision <= this.realtimeLastRevision) return;
            if (revision) this.realtimeLastRevision = revision;
            this.historyRefreshToken += 1;
            this.remoteChangePending = true;
            this.scheduleRealtimeRefresh(event, false);
        },
        scheduleRealtimeRefresh(event, immediate) {
            if (this.realtimeInteractionActive) {
                this.remoteChangePending = true;
                return;
            }
            if (this.realtimeRefreshTimer) {
                window.clearTimeout(this.realtimeRefreshTimer);
            }
            const actorName = event && event.actor &&
                event.actor.display_name || null;
            this.realtimeRefreshTimer = window.setTimeout(() => {
                this.realtimeRefreshTimer = null;
                this.refreshBoardFromRealtime(actorName);
            }, immediate ? 0 : 250);
        },
        flushQueuedRealtimeRefresh() {
            if (!this.remoteChangePending || this.realtimeInteractionActive) {
                return;
            }
            this.scheduleRealtimeRefresh(null, true);
        },
        async refreshBoardFromRealtime(actorName) {
            if (this.realtimeInteractionActive) {
                this.remoteChangePending = true;
                return;
            }
            const hadPendingSignal = this.remoteChangePending;
            const before = this.employeeFingerprintMap(this.board);
            const loaded = await this.loadBoard({
                silent: true,
                preserveUndo: true,
                fromRealtime: true
            });
            if (!loaded) return;
            this.remoteChangePending = false;
            const after = this.employeeFingerprintMap(this.board);
            const changedIds = [];
            const ids = new Set([...before.keys(), ...after.keys()]);
            ids.forEach(id => {
                if (before.get(id) !== after.get(id)) changedIds.push(id);
            });
            this.highlightChangedEmployees(changedIds);
            if (actorName || hadPendingSignal || changedIds.length) {
                this.showSyncNotice(
                    actorName
                        ? `Доску обновил ${actorName}`
                        : 'Доска обновлена'
                );
            }
        },
        showSyncNotice(message) {
            this.syncNotice = message;
            if (this.realtimeNoticeTimer) {
                window.clearTimeout(this.realtimeNoticeTimer);
            }
            this.realtimeNoticeTimer = window.setTimeout(() => {
                this.syncNotice = '';
                this.realtimeNoticeTimer = null;
            }, 3500);
        },
        employeeFingerprintMap(board) {
            const source = board || {};
            const values = new Map();
            const add = (employees, location) => {
                (Array.isArray(employees) ? employees : []).forEach(employee => {
                    const id = String(this.employeeId(employee) || '');
                    if (!id) return;
                    if (!values.has(id)) values.set(id, []);
                    values.get(id).push(JSON.stringify({
                        location,
                        lifecycle_status: employee.lifecycle_status || null,
                        priority: employee.priority || null,
                        department_id: employee.department_id || null,
                        position: employee.position || null,
                        absence: employee.absence || null,
                        planned_moves: employee.planned_moves || (
                            employee.planned_move ? [employee.planned_move] : []
                        ),
                        assignment: employee.assignment_context ||
                            employee.assignment || null,
                        visible_assignments: employee.visible_assignments || []
                    }));
                });
            };
            const lanes = source.lanes || {};
            add(lanes.home, 'home');
            add(lanes.requires_distribution, 'requires_distribution');
            add(lanes.reserve, 'reserve');
            (Array.isArray(source.groups) ? source.groups : []).forEach(group => {
                add(group.employees, `group:${group.id}`);
                (Array.isArray(group.objects) ? group.objects : []).forEach(object => {
                    add(object.employees, `object:${this.objectId(object)}`);
                });
            });
            (Array.isArray(source.standalone_objects)
                ? source.standalone_objects
                : []
            ).forEach(object => {
                add(object.employees, `object:${this.objectId(object)}`);
            });
            (Array.isArray(source.planned_objects)
                ? source.planned_objects
                : []
            ).forEach(object => {
                add(
                    object.employees,
                    `planned_object:${object.planned_object_id}`
                );
            });
            const fingerprints = new Map();
            values.forEach((parts, id) => {
                fingerprints.set(id, parts.sort().join('|'));
            });
            return fingerprints;
        },
        highlightChangedEmployees(employeeIds) {
            if (!this.$el || !employeeIds.length) return;
            this.$nextTick(() => {
                employeeIds.forEach(id => {
                    this.$el.querySelectorAll(
                        `[data-workforce-employee-id='${id}']`
                    ).forEach(element => {
                        element.classList.add(
                            'workforce-employee-card--remote-changed'
                        );
                    });
                });
                if (this.realtimeHighlightTimer) {
                    window.clearTimeout(this.realtimeHighlightTimer);
                }
                this.realtimeHighlightTimer = window.setTimeout(() => {
                    if (this.$el) {
                        this.$el.querySelectorAll(
                            '.workforce-employee-card--remote-changed'
                        ).forEach(element => {
                            element.classList.remove(
                                'workforce-employee-card--remote-changed'
                            );
                        });
                    }
                    this.realtimeHighlightTimer = null;
                }, 3800);
            });
        },
        revealMovedEmployee(element) {
            if (
                !element ||
                typeof element.scrollIntoView !== 'function' ||
                !element.isConnected
            ) {
                return false;
            }
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
            });
            return true;
        },
        highlightMovedEmployee(employeeId) {
            if (!this.$el || employeeId === null || employeeId === undefined) {
                return;
            }
            this.$nextTick(() => {
                const selector =
                    `[data-workforce-employee-id='${employeeId}']`;
                const cards = Array.from(
                    this.$el.querySelectorAll(selector)
                ).filter(element => (
                    !element.classList.contains(
                        'workforce-employee-card--planned-projection'
                    )
                ));
                const primaryCards = cards.filter(element => (
                    element.getAttribute('data-workforce-assignment-kind') ===
                    'primary'
                ));
                const targets = primaryCards.length
                    ? primaryCards
                    : cards.slice(0, 1);
                const scrollTarget = targets.find(element => (
                    typeof element.getClientRects === 'function' &&
                    element.getClientRects().length > 0
                )) || targets[0] || null;
                const startHighlight = () => {
                    targets.forEach(element => {
                        element.classList.remove(
                            'workforce-employee-card--just-moved'
                        );
                        void element.offsetWidth;
                        element.classList.add(
                            'workforce-employee-card--just-moved'
                        );
                    });
                    if (this.movementHighlightTimer) {
                        window.clearTimeout(this.movementHighlightTimer);
                    }
                    this.movementHighlightTimer = window.setTimeout(() => {
                        if (this.$el) {
                            this.$el.querySelectorAll(
                                '.workforce-employee-card--just-moved'
                            ).forEach(element => {
                                element.classList.remove(
                                    'workforce-employee-card--just-moved'
                                );
                            });
                        }
                        this.movementHighlightTimer = null;
                    }, 3200);
                };
                if (this.movementHighlightStartTimer) {
                    window.clearTimeout(this.movementHighlightStartTimer);
                    this.movementHighlightStartTimer = null;
                }
                if (this.revealMovedEmployee(scrollTarget)) {
                    this.movementHighlightStartTimer = window.setTimeout(() => {
                        this.movementHighlightStartTimer = null;
                        startHighlight();
                    }, 240);
                } else {
                    startHighlight();
                }
            });
        },
        restoreWorkspacePanelState() {
            const storage = this.browserStorage();
            if (!storage) {
                this.workspacePanelStateReady = true;
                return;
            }
            try {
                const raw = storage.getItem(this.workspacePanelStorageKey);
                if (raw) {
                    const state = JSON.parse(raw);
                    this.toolbarCollapsed = state.toolbarCollapsed === true;
                    this.scopePanelCollapsed = state.scopePanelCollapsed === true;
                    this.workspacePanelHintVisible = false;
                }
            } catch (error) {
                // Повреждённая локальная настройка не должна мешать работе доски.
            }
            this.workspacePanelStateReady = true;
        },
        restoreBoardDisplayPreferences() {
            const storage = this.browserStorage();
            if (!storage) return;
            try {
                this.boardDensity = storage.getItem(this.boardDensityStorageKey) ===
                    'overview'
                    ? 'overview'
                    : 'compact';
                this.overviewHintDismissed = storage.getItem(
                    this.overviewHintStorageKey
                ) === 'dismissed';
                if (this.overviewMode) {
                    this.toolbarCollapsed = true;
                    this.scopePanelCollapsed = true;
                }
            } catch (error) {
                this.boardDensity = 'compact';
                this.overviewHintDismissed = false;
            }
        },
        persistBoardDisplayPreferences() {
            const storage = this.browserStorage();
            if (!storage) return;
            try {
                storage.setItem(
                    this.boardDensityStorageKey,
                    this.overviewMode ? 'overview' : 'compact'
                );
            } catch (error) {
                // Недоступный localStorage не должен менять работу доски.
            }
        },
        dismissOverviewHint() {
            this.overviewHintDismissed = true;
            const storage = this.browserStorage();
            if (!storage) return;
            try {
                storage.setItem(this.overviewHintStorageKey, 'dismissed');
            } catch (error) {
                // Подсказка останется только до обновления страницы.
            }
        },
        setBoardDensity(mode) {
            const nextMode = mode === 'overview' ? 'overview' : 'compact';
            const wasOverview = this.overviewMode;
            this.boardDensity = nextMode;
            if (nextMode !== 'overview') {
                if (wasOverview) {
                    this.toolbarCollapsed = false;
                    this.scopePanelCollapsed = false;
                    requestDesktopSidebarCollapsed(false, {temporary: true});
                    this.$nextTick(this.measureToolbar);
                }
                return;
            }
            this.toolbarCollapsed = true;
            this.scopePanelCollapsed = true;
            requestDesktopSidebarCollapsed(true, {temporary: true});
            this.mobileToolbarExpanded = false;
            this.boardScopeFilter = null;
            this.boardScopeResetToken += 1;
        },
        persistWorkspacePanelState() {
            if (!this.workspacePanelStateReady) return;
            const storage = this.browserStorage();
            if (!storage) return;
            try {
                storage.setItem(this.workspacePanelStorageKey, JSON.stringify({
                    toolbarCollapsed: this.toolbarCollapsed,
                    scopePanelCollapsed: this.scopePanelCollapsed
                }));
            } catch (error) {
                // Доска остаётся рабочей, даже если localStorage недоступен.
            }
        },
        toggleWorkspacePanels() {
            const collapsed = !this.workspacePanelsCollapsed;
            this.workspacePanelHintVisible = false;
            this.toolbarCollapsed = collapsed;
            this.scopePanelCollapsed = collapsed;
            requestDesktopSidebarCollapsed(collapsed, {temporary: true});
        },
        setScopePanelCollapsed(value) {
            this.scopePanelCollapsed = value === true;
        },
        async initialize() {
            await this.loadBoard();
        },
        showToday() {
            if (this.boardMutationPending || this.loading) {
                return;
            }
            this.boardDate = currentLocalDate();
            this.loadBoard();
        },
        formatBoardDate(value) {
            const match = String(value || '').match(
                /^(\d{4})-(\d{2})-(\d{2})$/
            );
            return match
                ? `${match[3]}.${match[2]}.${match[1]}`
                : String(value || '');
        },
        searchResultElements() {
            if (!this.searchActive || !this.$el) {
                return [];
            }
            return Array.from(this.$el.querySelectorAll(
                '[data-workforce-search-result="true"]'
            )).filter(element => (
                element &&
                typeof element.getClientRects === 'function' &&
                element.getClientRects().length > 0
            ));
        },
        clearCurrentSearchResult() {
            if (!this.$el) {
                return;
            }
            this.$el.querySelectorAll(
                '.workforce-employee-card--search-current'
            ).forEach(element => {
                element.classList.remove(
                    'workforce-employee-card--search-current'
                );
            });
        },
        applyCurrentSearchResult(scroll = false) {
            this.clearCurrentSearchResult();
            const elements = this.searchResultElements();
            const current = elements[this.searchResultIndex];
            if (!current) {
                return;
            }
            current.classList.add('workforce-employee-card--search-current');
            if (scroll && typeof current.scrollIntoView === 'function') {
                current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                });
            }
        },
        refreshSearchResults(resetPosition = false) {
            const elements = this.searchResultElements();
            this.searchResultCount = elements.length;
            if (
                resetPosition ||
                !elements.length ||
                this.searchResultIndex >= elements.length
            ) {
                this.searchResultIndex = -1;
            }
            this.$nextTick(() => {
                this.applyCurrentSearchResult(false);
            });
        },
        scheduleSearchResultsRefresh(resetPosition = false) {
            this.$nextTick(() => {
                this.refreshSearchResults(resetPosition);
            });
        },
        navigateSearchResults(direction = 1) {
            if (!this.searchActive) {
                return;
            }
            this.$nextTick(() => {
                const elements = this.searchResultElements();
                this.searchResultCount = elements.length;
                if (!elements.length) {
                    this.searchResultIndex = -1;
                    this.clearCurrentSearchResult();
                    return;
                }
                if (this.searchResultIndex < 0) {
                    this.searchResultIndex = direction < 0
                        ? elements.length - 1
                        : 0;
                } else {
                    this.searchResultIndex = (
                        this.searchResultIndex +
                        direction +
                        elements.length
                    ) % elements.length;
                }
                this.$nextTick(() => {
                    this.applyCurrentSearchResult(true);
                });
            });
        },
        onBoardScopeChange(value) {
            this.boardScopeFilter = value || null;
            this.scheduleSearchResultsRefresh(true);
        },
        clearFilterChip(key) {
            if (key === 'date') {
                this.showToday();
            } else if (key === 'search') {
                this.filters.query = '';
            } else if (key === 'department') {
                this.filters.departmentId = '';
            } else if (key === 'priority') {
                this.filters.priority = '';
            } else if (key === 'hiringForm') {
                this.filters.hiringForm = '';
            } else if (key === 'itr') {
                this.filters.itr = false;
            } else if (key === 'skills' || key === 'positions') {
                this.filters[key] = [];
            } else if (key === 'citizenship') {
                this.filters.citizenship = '';
            } else if (key === 'scope') {
                this.resetBoardScopeFilter();
            }
        },
        resetBoardScopeFilter() {
            this.boardScopeFilter = null;
            this.boardScopeResetToken += 1;
        },
        clearAllFilters() {
            if (this.boardMutationPending || this.loading) {
                return;
            }
            this.filters.query = '';
            this.filters.departmentId = '';
            this.filters.priority = '';
            this.filters.citizenship = '';
            this.filters.hiringForm = '';
            this.filters.skills = [];
            this.filters.itr = false;
            this.filters.positions = [];
            if (this.boardScopeFilter) {
                this.resetBoardScopeFilter();
            }
            if (!this.boardIsToday) {
                this.boardDate = currentLocalDate();
                this.loadBoard();
            }
        },
        async loadBoard(options = {}) {
            if (!this.selectedDateIsValid) {
                return false;
            }
            const silent = Boolean(options && options.silent === true);
            const preserveUndo = Boolean(
                options && options.preserveUndo === true
            );
            if (!preserveUndo) {
                this.clearUndoState();
            }
            const requestId = this.boardRequestId + 1;
            const requestedDate = this.boardDate;
            this.boardRequestId = requestId;
            if (!silent) {
                this.loading = true;
                this.errorMessage = '';
                this.stopDragging();
            }
            try {
                const response = await api.get('/api/employees/workforce/board', {
                    params: {date: requestedDate},
                    ignoreModal: true
                });
                const raw = response && response.data && response.data.data
                    ? response.data.data
                    : (response ? response.data : null);
                if (
                    requestId !== this.boardRequestId ||
                    requestedDate !== this.boardDate
                ) {
                    return false;
                }
                this.board = this.normalizeBoard(raw);
                if (this.$refs.timesheetIncidents) {
                    this.$refs.timesheetIncidents.load();
                }
                this.hasLoaded = true;
                this.accessDenied = false;
                this.refreshSelectedEmployee();
                this.refreshAssignmentManagerEmployee();
                this.scheduleSearchResultsRefresh(false);
                return true;
            } catch (error) {
                if (requestId !== this.boardRequestId) {
                    return false;
                }
                if (error && error.response && error.response.status === 403) {
                    this.accessDenied = true;
                } else {
                    this.errorMessage = this.errorText(
                        error,
                        'Не удалось загрузить распределение сотрудников'
                    );
                }
                return false;
            } finally {
                // A silent refresh can supersede a foreground load and must release its loading state.
                if (requestId === this.boardRequestId) {
                    this.loading = false;
                }
            }
        },
        normalizeBoard(raw) {
            const source = raw && typeof raw === 'object' ? raw : {};
            const normalized = emptyBoard(source.date || this.boardDate);
            normalized.permissions = source.permissions && typeof source.permissions === 'object'
                ? source.permissions
                : {};
            normalized.plan_mode = source.plan_mode === 'workforce_plan'
                ? 'workforce_plan'
                : 'fact';
            normalized.settings = source.settings && typeof source.settings === 'object'
                ? source.settings
                : {};
            normalized.groups = Array.isArray(source.groups)
                ? source.groups.map(group => Object.assign({}, group, {
                    employees: Array.isArray(group.employees) ? group.employees : [],
                    objects: Array.isArray(group.objects)
                        ? group.objects.map(object => Object.assign({}, object, {
                            employees: Array.isArray(object.employees) ? object.employees : []
                        }))
                        : []
                }))
                : [];
            normalized.standalone_objects = Array.isArray(source.standalone_objects)
                ? source.standalone_objects.map(object => Object.assign({}, object, {
                    employees: Array.isArray(object.employees) ? object.employees : []
                }))
                : [];
            normalized.planned_objects = Array.isArray(source.planned_objects)
                ? source.planned_objects.map(object => Object.assign({}, object, {
                    employees: Array.isArray(object.employees) ? object.employees : []
                }))
                : [];
            const lanes = source.lanes && typeof source.lanes === 'object' ? source.lanes : {};
            normalized.lanes = {
                home: Array.isArray(lanes.home) ? lanes.home : [],
                requires_distribution: Array.isArray(lanes.requires_distribution)
                    ? lanes.requires_distribution
                    : [],
                rest: Array.isArray(lanes.rest) ? lanes.rest : [],
                loupe: Array.isArray(lanes.loupe) ? lanes.loupe : [],
                reserve: Array.isArray(lanes.reserve) ? lanes.reserve : []
            };
            normalized.departments = Array.isArray(source.departments) ? source.departments : [];
            return normalized;
        },
        errorText(error, fallback) {
            const data = error && error.response ? error.response.data : null;
            if (data && typeof data === 'object') {
                return data.msg || data.message || fallback;
            }
            if (typeof data === 'string' && data) {
                return data;
            }
            return fallback;
        },
        onResize() {
            this.viewportWidth = window.innerWidth;
            this.viewportHeight = window.innerHeight;
            this.measureToolbar();
        },
        syncOverviewLandscapeShell() {
            if (typeof document === 'undefined' || !document.body) return;
            document.body.classList.toggle(
                OVERVIEW_LANDSCAPE_BODY_CLASS,
                this.mobileOverviewLandscape
            );
        },
        clearOverviewLandscapeShell() {
            if (typeof document === 'undefined' || !document.body) return;
            document.body.classList.remove(OVERVIEW_LANDSCAPE_BODY_CLASS);
        },
        toggleMobileToolbar() {
            const expanded = !this.mobileToolbarExpanded;
            this.mobileToolbarExpanded = expanded;
            this.mobileToolbarAutoCloseReady = false;
            if (this.mobileToolbarAutoCloseTimer) {
                window.clearTimeout(this.mobileToolbarAutoCloseTimer);
                this.mobileToolbarAutoCloseTimer = null;
            }
            if (expanded) {
                this.mobileToolbarAutoCloseTimer = window.setTimeout(() => {
                    this.mobileToolbarAutoCloseReady = true;
                    this.mobileToolbarAutoCloseTimer = null;
                }, 250);
            }
        },
        onBoardScroll() {
            this.closeContextMenu();
            if (
                this.isMobileToolbarLayout &&
                this.mobileToolbarExpanded &&
                this.mobileToolbarAutoCloseReady
            ) {
                this.mobileToolbarExpanded = false;
                this.mobileToolbarAutoCloseReady = false;
            }
        },
        observeToolbar() {
            this.$nextTick(() => {
                this.measureToolbar();
                if (
                    typeof window.ResizeObserver !== 'function' ||
                    !this.$refs.toolbar
                ) {
                    return;
                }
                if (this.toolbarResizeObserver) {
                    this.toolbarResizeObserver.disconnect();
                }
                this.toolbarResizeObserver = new window.ResizeObserver(
                    this.measureToolbar
                );
                this.toolbarResizeObserver.observe(this.$refs.toolbar);
            });
        },
        measureToolbar() {
            const toolbar = this.$refs.toolbar;
            this.toolbarHeight = toolbar
                ? Math.ceil(toolbar.getBoundingClientRect().height)
                : 0;
        },
        onKeydown(event) {
            if (event.key !== 'Escape') {
                return;
            }
            if (this.contextMenu.visible) {
                this.closeContextMenu();
                return;
            }
            if (this.historyPanelVisible) {
                this.closeWorkforceHistory();
                return;
            }
            if (this.selectedEmployee) {
                this.closeInspector();
            }
        },
        pushEmployees(target, employees) {
            if (Array.isArray(employees)) {
                target.push(...employees);
            }
        },
        allEmployeeCards() {
            const employees = [];
            this.pushEmployees(employees, this.lanes.home);
            this.pushEmployees(employees, this.lanes.requires_distribution);
            this.pushEmployees(employees, this.lanes.reserve);
            this.groups.forEach(group => {
                this.pushEmployees(employees, group.employees);
                (Array.isArray(group.objects) ? group.objects : []).forEach(object => {
                    this.pushEmployees(employees, object.employees);
                });
            });
            this.standaloneObjects.forEach(object => {
                this.pushEmployees(employees, object.employees);
            });
            return employees;
        },
        uniqueEmployees(employees) {
            const seen = {};
            return employees.filter(employee => {
                const id = this.employeeId(employee);
                const key = id === null || id === undefined
                    ? `anonymous-${this.employeeName(employee)}`
                    : String(id);
                if (seen[key]) {
                    return false;
                }
                seen[key] = true;
                return true;
            });
        },
        employeeId(employee) {
            return employee ? (employee.employee_id || employee.id) : null;
        },
        employeeAssignment(employee) {
            return employee
                ? (employee.assignment || employee.assignment_context || null)
                : null;
        },
        assignmentId(assignment) {
            return assignment
                ? (assignment.assignment_id || assignment.id || null)
                : null;
        },
        assignmentKind(assignment) {
            if (!assignment) {
                return '';
            }
            if (assignment.kind) {
                return String(assignment.kind);
            }
            if (assignment.assignment_kind) {
                return String(assignment.assignment_kind);
            }
            return assignment.is_primary === true ? 'primary' : 'additional';
        },
        assignmentIsPrimary(assignment) {
            return this.assignmentKind(assignment) !== 'additional';
        },
        employeePrimaryAssignment(employee) {
            const assignments = employee && Array.isArray(employee.visible_assignments)
                ? employee.visible_assignments
                : [];
            return assignments.find(assignment => (
                this.assignmentKind(assignment) === 'primary'
            )) || (
                this.assignmentIsPrimary(this.employeeAssignment(employee))
                    ? this.employeeAssignment(employee)
                    : null
            );
        },
        employeeHasAdditionalTarget(employee, target) {
            if (!employee || !target || target.type !== 'object') {
                return false;
            }
            const assignments = Array.isArray(employee.visible_assignments)
                ? employee.visible_assignments
                : [];
            return assignments.some(assignment => (
                this.assignmentKind(assignment) === 'additional' &&
                this.sameTarget(this.assignmentTarget(assignment), target)
            ));
        },
        assignmentTarget(assignment) {
            if (!assignment) {
                return null;
            }
            const type = assignment.target_type || assignment.type;
            const id = assignment.target_id ||
                assignment.building_id ||
                assignment.object_group_id ||
                null;
            return type ? {type, id} : null;
        },
        assignmentKey(assignment) {
            if (!assignment) {
                return '';
            }
            const kind = this.assignmentKind(assignment) || 'unknown';
            const assignmentId = this.assignmentId(assignment);
            if (assignmentId !== null && assignmentId !== undefined) {
                return `${kind}:${assignmentId}`;
            }
            const target = this.assignmentTarget(assignment);
            return [
                kind,
                target && target.type || 'none',
                target && target.id || 'common'
            ].join(':');
        },
        sameAssignment(left, right) {
            const leftKey = this.assignmentKey(left);
            return Boolean(leftKey && leftKey === this.assignmentKey(right));
        },
        employeeCardId(employee) {
            return this.employeeId(employee);
        },
        employeeName(employee) {
            return employee
                ? (employee.display_name ||
                    employee.full_name ||
                    employee.name ||
                    `Сотрудник #${this.employeeId(employee) || ''}`)
                : '';
        },
        objectId(object) {
            return object.building_id || object.object_id || object.id;
        },
        filteredLane(name) {
            return this.filteredEmployees(this.lanes[name]);
        },
        filteredEmployees(employees) {
            return (Array.isArray(employees) ? employees : []).filter(this.employeeMatchesFilters);
        },
        employeeMatchesFilters(employee) {
            if (!matchesEmployeeFilters(employee, this.filters.skills, this.filters.positions, this.filters.itr)) return false;
            if (this.filters.departmentId) {
                const departmentIds = Array.isArray(employee.department_ids)
                    ? employee.department_ids
                    : [
                        employee.operational_department_id ||
                        employee.department_id ||
                        (
                            employee.department &&
                            (employee.department.department_id || employee.department.id)
                        )
                    ];
                if (!departmentIds.some(departmentId => (
                    String(departmentId || '') === String(this.filters.departmentId)
                ))) {
                    return false;
                }
            }
            if (this.filters.priority) {
                const priority = employee.priority;
                if (this.filters.priority === '__none__') {
                    if (priority !== null && priority !== undefined && priority !== '') {
                        return false;
                    }
                } else if (String(priority) !== String(this.filters.priority)) {
                    return false;
                }
            }
            if (!matchesHiringForm(employee, this.filters.hiringForm)) return false;
            if (this.filters.citizenship) {
                const citizenship = String(employee.citizenship || '').trim();
                if (this.filters.citizenship === '__none__') {
                    if (citizenship) return false;
                } else if (citizenship !== this.filters.citizenship) {
                    return false;
                }
            }
            const query = String(this.filters.query || '').trim().toLocaleLowerCase('ru-RU');
            if (!query) {
                return true;
            }
            const contacts = Array.isArray(employee.contacts)
                ? employee.contacts.map(contact => (
                    typeof contact === 'string'
                        ? contact
                        : [contact.label, contact.value, contact.contact].filter(Boolean).join(' ')
                )).join(' ')
                : '';
            const haystack = [
                this.employeeName(employee),
                employee.middle_name,
                employee.position,
                employee.citizenship,
                ...(Array.isArray(employee.skills) ? employee.skills : []),
                employee.department_name,
                ...(Array.isArray(employee.department_names)
                    ? employee.department_names
                    : []),
                contacts
            ].filter(Boolean).join(' ').toLocaleLowerCase('ru-RU');
            return query.split(/\s+/).every(token => haystack.includes(token));
        },
        canOpenEmployeeCard(employee) {
            return Boolean(employee && employee.can_open_card === true);
        },
        selectedCardCanManage(section) {
            const manage = this.selectedEmployee
                && this.selectedEmployee.card_access
                && this.selectedEmployee.card_access.manage;
            return Boolean(manage && manage[section] === true);
        },
        async openInspector(employee) {
            this.closeContextMenu();
            if (!this.selectedEmployee) {
                this.pushInspectorHistoryEntry();
            }
            this.selectedEmployee = employee;
            const employeeId = this.employeeId(employee);
            if (!employeeId) {
                this.inspectorLoading = false;
                return;
            }
            const requestId = this.inspectorRequestId + 1;
            this.inspectorRequestId = requestId;
            this.inspectorLoading = true;
            try {
                const response = await api.get(
                    `/api/employees/workforce/board/employee/${encodeURIComponent(employeeId)}`,
                    {
                        params: {date: this.boardDate},
                        ignoreModal: true
                    }
                );
                if (
                    requestId !== this.inspectorRequestId
                    || !this.selectedEmployee
                    || String(this.employeeId(this.selectedEmployee)) !== String(employeeId)
                ) return;
                this.selectedEmployee = Object.assign(
                    {},
                    this.selectedEmployee,
                    response.data || {}
                );
            } catch (error) {
                // Базовая карточка доски уже открыта; расширенные данные необязательны.
            } finally {
                if (requestId === this.inspectorRequestId) {
                    this.inspectorLoading = false;
                }
            }
        },
        browserHistoryToken(stateKey) {
            return typeof window !== 'undefined'
                && window.history
                && window.history.state
                && window.history.state[stateKey];
        },
        pushBrowserHistoryEntry(stateKey) {
            if (
                typeof window === 'undefined'
                || !window.history
                || typeof window.history.pushState !== 'function'
            ) {
                return null;
            }
            const token = `${Date.now()}:${Math.random().toString(36).slice(2)}`;
            const state = Object.assign({}, window.history.state || {}, {
                [stateKey]: token
            });
            try {
                window.history.pushState(state, document.title, window.location.href);
                return token;
            } catch (error) {
                return null;
            }
        },
        closeBrowserHistoryEntry(stateKey, token, close) {
            const active = token && this.browserHistoryToken(stateKey) === token;
            close();
            if (
                active
                && typeof window.history.back === 'function'
            ) {
                window.history.back();
            }
        },
        openEmployeeCardFromOverlay(options) {
            const opts = options || {};
            if (!opts.id) return;
            const returnView = this.captureWorkforceReturnView();
            const active = opts.token
                && this.browserHistoryToken(opts.stateKey) === opts.token;
            opts.close();
            const navigate = () => {
                this.writeWorkforceReturnHistoryState(returnView);
                this.navigateToEmployeeCard(opts.id, opts.date || this.boardDate);
            };
            if (active && typeof window.history.back === 'function') {
                window.addEventListener('popstate', () => {
                    window.setTimeout(navigate, 0);
                }, {once: true});
                window.history.back();
                return;
            }
            navigate();
        },
        openWorkforceHistory() {
            if (this.historyPanelVisible) return;
            this.historyPanelVisible = true;
            if (!this.historyPanelToken) {
                this.historyPanelToken = this.pushBrowserHistoryEntry(
                    WORKFORCE_HISTORY_PANEL_STATE_KEY
                );
            }
        },
        closeWorkforceHistory() {
            const token = this.historyPanelToken;
            this.closeBrowserHistoryEntry(
                WORKFORCE_HISTORY_PANEL_STATE_KEY,
                token,
                () => {
                    this.historyPanelVisible = false;
                    this.historyPanelToken = null;
                }
            );
        },
        openEmployeeFromHistory(employee) {
            const id = employee && employee.employee_id;
            this.openEmployeeCardFromOverlay({
                id,
                date: this.boardDate,
                stateKey: WORKFORCE_HISTORY_PANEL_STATE_KEY,
                token: this.historyPanelToken,
                close: () => {
                    this.historyPanelVisible = false;
                    this.historyPanelToken = null;
                }
            });
        },
        pushInspectorHistoryEntry() {
            if (!this.inspectorHistoryToken) {
                this.inspectorHistoryToken = this.pushBrowserHistoryEntry(
                    INSPECTOR_HISTORY_STATE_KEY
                );
            }
        },
        onInspectorHistoryPopState(event) {
            if (this.historyPanelToken) {
                const historyToken = event && event.state
                    && event.state[WORKFORCE_HISTORY_PANEL_STATE_KEY];
                if (historyToken !== this.historyPanelToken) {
                    this.historyPanelToken = null;
                    this.historyPanelVisible = false;
                }
            }
            if (!this.inspectorHistoryToken) {
                return;
            }
            const token = event && event.state &&
                event.state[INSPECTOR_HISTORY_STATE_KEY];
            if (token === this.inspectorHistoryToken) {
                return;
            }
            this.inspectorHistoryToken = null;
            this.inspectorRequestId += 1;
            this.inspectorLoading = false;
            this.selectedEmployee = null;
        },
        async onEmployeeCreated(employee) {
            await this.loadBoard({silent: true});
            const name = employee && (
                employee.display_name
                || employee.full_name
                || [employee.last_name, employee.first_name].filter(Boolean).join(' ')
            );
            this.$bvToast.toast(
                name ? `Сотрудник «${name}» создан` : 'Сотрудник создан',
                {variant: 'success', solid: true, autoHideDelay: 2200}
            );
        },
        closeInspector() {
            this.inspectorRequestId += 1;
            this.inspectorLoading = false;
            const token = this.inspectorHistoryToken;
            this.closeBrowserHistoryEntry(
                INSPECTOR_HISTORY_STATE_KEY,
                token,
                () => {
                    this.selectedEmployee = null;
                    this.inspectorHistoryToken = null;
                }
            );
        },
        refreshSelectedEmployee() {
            if (!this.selectedEmployee) {
                return;
            }
            const id = this.employeeId(this.selectedEmployee);
            const selectedAssignmentKey = this.assignmentKey(
                this.employeeAssignment(this.selectedEmployee)
            );
            const selectedAssignmentKind = this.assignmentKind(
                this.employeeAssignment(this.selectedEmployee)
            );
            const candidates = this.allEmployeeCards().filter(employee => (
                String(this.employeeId(employee)) === String(id)
            ));
            const exactAssignment = candidates.find(employee => (
                selectedAssignmentKey &&
                this.assignmentKey(this.employeeAssignment(employee)) ===
                    selectedAssignmentKey
            ));
            const sameAssignmentKind = candidates.find(employee => (
                selectedAssignmentKind &&
                this.assignmentKind(this.employeeAssignment(employee)) ===
                    selectedAssignmentKind
            ));
            const nextEmployee = exactAssignment || sameAssignmentKind ||
                candidates.find(employee => (
                String(this.employeeId(employee)) === String(id) &&
                    this.assignmentKind(this.employeeAssignment(employee)) === 'primary'
            )) || this.allEmployees.find(employee => (
                String(this.employeeId(employee)) === String(id)
            )) || null;
            const skills = this.selectedEmployee && this.selectedEmployee.skills;
            this.selectedEmployee = nextEmployee && Array.isArray(skills)
                ? Object.assign({}, nextEmployee, {skills})
                : nextEmployee;
        },
        refreshAssignmentManagerEmployee() {
            if (
                !this.assignmentManagerVisible ||
                !this.assignmentManagerEmployee ||
                this.assignmentManagerDirty
            ) {
                return;
            }
            const employeeId = this.employeeId(this.assignmentManagerEmployee);
            const candidates = this.allEmployeeCards().filter(employee => (
                String(this.employeeId(employee)) === String(employeeId)
            ));
            this.assignmentManagerEmployee = candidates.find(employee => (
                this.assignmentKind(this.employeeAssignment(employee)) === 'primary'
            )) || candidates[0] || null;
        },
        navigateToEmployeeCard(id, date) {
            this.$router.push({
                path: `/employees/${id}`,
                query: {
                    date,
                    from: 'workforce'
                }
            }).catch(error => {
                if (!error || error.name !== 'NavigationDuplicated') throw error;
            });
        },
        openEmployeeCard(id, date = this.boardDate) {
            const selectedId = this.employeeId(this.selectedEmployee);
            if (
                selectedId
                && String(selectedId) === String(id)
                && !this.canOpenEmployeeCard(this.selectedEmployee)
            ) return;
            this.openEmployeeCardFromOverlay({
                id,
                date,
                stateKey: INSPECTOR_HISTORY_STATE_KEY,
                token: this.inspectorHistoryToken,
                close: () => {
                    this.inspectorRequestId += 1;
                    this.inspectorLoading = false;
                    this.selectedEmployee = null;
                    this.inspectorHistoryToken = null;
                }
            });
        },
        openEmployeeRegistry() {
            this.$router.push({name: 'employees'}).catch(error => {
                if (!error || error.name !== 'NavigationDuplicated') throw error;
            });
        },
        openSettings() {
            this.$router.push({
                name: 'employees-workforce-settings'
            }).catch(error => {
                if (!error || error.name !== 'NavigationDuplicated') throw error;
            });
        },
        openContextMenu(event, employee) {
            if (this.isMobileLayout) {
                this.openInspector(employee);
                return;
            }
            const width = 230;
            const height = 132;
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            this.contextMenu = {
                visible: true,
                x: Math.max(8, Math.min(event.clientX, viewportWidth - width - 8)),
                y: Math.max(8, Math.min(event.clientY, viewportHeight - height - 8)),
                employee
            };
        },
        closeContextMenu() {
            if (this.contextMenu.visible) {
                this.contextMenu.visible = false;
            }
        },
        openInspectorFromContext() {
            const employee = this.contextMenu.employee;
            this.closeContextMenu();
            this.openInspector(employee);
        },
        moveFromContext() {
            const employee = this.contextMenu.employee;
            this.closeContextMenu();
            this.openAssignmentManager(employee);
        },
        openCardFromContext() {
            const id = this.employeeCardId(this.contextMenu.employee);
            this.closeContextMenu();
            if (id) {
                this.openEmployeeCard(id);
            }
        },
        startDragging(employee) {
            const reserve = this.employeeInReserve(employee);
            if (reserve && !this.canReturnReserveEmployee(employee)) {
                return;
            }
            if (
                !reserve &&
                (
                    !this.canMoveEmployee(employee) ||
                    !this.assignmentIsPrimary(this.employeeAssignment(employee))
                )
            ) {
                return;
            }
            this.closeContextMenu();
            this.draggingEmployee = employee;
            this.draggingEmployeeId = this.employeeId(employee);
            this.draggingAssignmentId = this.assignmentKey(
                this.employeeAssignment(employee)
            );
        },
        onEmployeeDragOver(event) {
            if (
                !this.draggingEmployee ||
                !event ||
                typeof window === 'undefined'
            ) {
                return;
            }
            const viewportHeight = window.innerHeight ||
                document.documentElement.clientHeight ||
                0;
            if (!viewportHeight) {
                return;
            }
            const edgeSize = Math.min(
                120,
                Math.max(64, Math.round(viewportHeight * 0.14))
            );
            const pointerY = Number(event.clientY);
            let speed = 0;
            if (pointerY < edgeSize) {
                const ratio = (edgeSize - Math.max(0, pointerY)) / edgeSize;
                speed = -Math.ceil(4 + (24 * ratio));
            } else if (pointerY > viewportHeight - edgeSize) {
                const ratio = (
                    Math.min(viewportHeight, pointerY) -
                    (viewportHeight - edgeSize)
                ) / edgeSize;
                speed = Math.ceil(4 + (24 * ratio));
            }
            this.dragAutoScrollSpeed = speed;
            if (speed) {
                this.startEmployeeDragAutoScroll();
            } else {
                this.stopEmployeeDragAutoScroll();
            }
        },
        startEmployeeDragAutoScroll() {
            if (
                this.dragAutoScrollFrame !== null ||
                !this.dragAutoScrollSpeed ||
                !this.draggingEmployee ||
                typeof window === 'undefined' ||
                typeof window.requestAnimationFrame !== 'function'
            ) {
                return;
            }
            this.dragAutoScrollFrame = window.requestAnimationFrame(
                this.runEmployeeDragAutoScroll
            );
        },
        runEmployeeDragAutoScroll() {
            this.dragAutoScrollFrame = null;
            if (!this.draggingEmployee || !this.dragAutoScrollSpeed) {
                return;
            }
            window.scrollBy(0, this.dragAutoScrollSpeed);
            this.startEmployeeDragAutoScroll();
        },
        stopEmployeeDragAutoScroll() {
            if (
                this.dragAutoScrollFrame !== null &&
                typeof window !== 'undefined' &&
                typeof window.cancelAnimationFrame === 'function'
            ) {
                window.cancelAnimationFrame(this.dragAutoScrollFrame);
            }
            this.dragAutoScrollFrame = null;
            this.dragAutoScrollSpeed = 0;
        },
        stopDragging() {
            this.stopEmployeeDragAutoScroll();
            this.draggingEmployee = null;
            this.draggingEmployeeId = null;
            this.draggingAssignmentId = null;
            this.$nextTick(this.flushQueuedRealtimeRefresh);
        },
        async dropEmployee(target) {
            const employee = this.draggingEmployee;
            this.stopDragging();
            if (!employee || !target) {
                return;
            }
            if (this.employeeInReserve(employee)) {
                if (
                    !this.canReturnReserveEmployee(employee) ||
                    !this.canUseTarget(target)
                ) {
                    return;
                }
                const confirmed = await this.confirmReserveReturn(employee, target);
                if (!confirmed) {
                    return;
                }
                await this.moveEmployee(
                    employee,
                    target,
                    '',
                    {returnFromReserve: true}
                );
                return;
            }
            await this.moveEmployee(employee, target);
        },
        employeeInReserve(employee) {
            if (!employee) {
                return false;
            }
            const id = this.employeeId(employee);
            return (Array.isArray(this.lanes.reserve) ? this.lanes.reserve : []).some(item => (
                String(this.employeeId(item)) === String(id)
            ));
        },
        canMoveEmployee(employee) {
            return Boolean(
                employee &&
                this.movementAllowed &&
                employee.can_move !== false &&
                !this.employeeInReserve(employee)
            );
        },
        canConfirmPlannedMove(employee) {
            return Boolean(
                employee &&
                this.movementAllowed &&
                !this.employeeInReserve(employee) &&
                (
                    employee.can_confirm_planned_move === true ||
                    employee.can_move !== false
                )
            );
        },
        canConfirmPlannedMoveAtTarget(move) {
            if (!move || move.target_id === null || move.target_id === undefined) {
                return false;
            }
            const targetId = Number(move.target_id);
            if (move.target_type === 'object_group') {
                const group = this.groups.find(item => Number(item.id) === targetId);
                return Boolean(group && group.can_move === true);
            }
            if (move.target_type !== 'object') return false;
            const groupedObject = this.groups.reduce((found, group) => (
                found || (group.objects || []).find(item => (
                    Number(item.id || item.building_id) === targetId
                ))
            ), null);
            const object = groupedObject || this.standaloneObjects.find(item => (
                Number(item.id || item.building_id) === targetId
            ));
            return Boolean(object && object.can_move === true);
        },
        canReturnReserveEmployee(employee) {
            return Boolean(
                employee &&
                this.employeeInReserve(employee) &&
                this.reserveMovementAllowed &&
                employee.can_move !== false
            );
        },
        async confirmReserveReturn(employee, target, date) {
            this.reserveReturnEmployeeId = this.employeeId(employee);
            this.reserveReturnDate = date || this.boardDate;
            this.reserveReturnVisible = true;
            return false;
        },
        async reserveReturned() {
            await this.loadBoard();
            if (this.assignmentManagerEmployee && this.employeeId(this.assignmentManagerEmployee) === this.reserveReturnEmployeeId) {
                const fresh = this.allEmployeeCards().find(e => this.employeeId(e) === this.reserveReturnEmployeeId);
                if (fresh) this.assignmentManagerEmployee = fresh;
            }
            this.assignmentActionError = '';
        },
        openAssignmentManager(employee) {
            if (
                !employee ||
                (
                    this.employeeInReserve(employee) &&
                    !this.canReturnReserveEmployee(employee)
                )
            ) {
                return;
            }
            this.closeContextMenu();
            this.assignmentActionError = '';
            this.assignmentManagerEmployee = employee;
            this.assignmentManagerTarget = employee.timesheet_target || null;
            this.assignmentManagerVisible = true;
        },
        openAssignmentManagerForTarget(target) {
            if (
                !target ||
                (target.type !== 'object' && target.type !== 'object_group') ||
                !this.canUseTarget(target)
            ) {
                return;
            }
            this.closeContextMenu();
            this.assignmentManagerEmployee = null;
            this.assignmentManagerTarget = target;
            this.assignmentManagerVisible = true;
        },
        closeAssignmentManager() {
            if (this.movementPending) {
                return;
            }
            this.assignmentManagerVisible = false;
            this.assignmentManagerEmployee = null;
            this.assignmentManagerTarget = null;
            this.assignmentManagerDirty = false;
            this.$nextTick(this.flushQueuedRealtimeRefresh);
        },
        openEmployeeCardFromAssignmentManager(employee) {
            const id = this.employeeCardId(employee);
            if (!id || this.movementPending) {
                return;
            }
            this.closeAssignmentManager();
            this.$nextTick(() => {
                this.openEmployeeCard(id);
            });
        },
        setAssignmentManagerDirty(value) {
            this.assignmentManagerDirty = value === true;
            if (!this.assignmentManagerDirty) {
                this.$nextTick(this.flushQueuedRealtimeRefresh);
            }
        },
        async movePrimaryFromManager(payload) {
            const value = payload || {};
            if (!value.employee || !value.target) {
                return;
            }
            const reserveEmployee = this.employeeInReserve(value.employee);
            if (reserveEmployee) {
                const confirmed = await this.confirmReserveReturn(
                    value.employee,
                    value.target, value.effectiveDate
                );
                if (!confirmed) {
                    return;
                }
            }
            const targetOnly = Boolean(
                this.assignmentManagerTarget &&
                this.sameTarget(this.assignmentManagerTarget, value.target)
            );
            this.assignmentActionError = '';
            const moved = await this.moveEmployee(
                value.employee,
                value.target,
                value.note || '',
                {
                    allowTargetOnly: targetOnly,
                    effectiveDate: value.effectiveDate,
                    returnFromReserve: reserveEmployee
                }
            );
            if (!moved) this.assignmentActionError = this.errorMessage;
            if (moved) {
                this.closeAssignmentManager();
            }
        },
        async assignmentChanged() {
            this.closeAssignmentManager();
            await this.loadBoard();
            this.remoteChangePending = false;
        },
        async onInspectorCharacteristicAdded() {
            this.historyRefreshToken += 1;
            await this.loadBoard();
        },
        async onInspectorReserveReturned() {
            const employee = this.selectedEmployee;
            await this.loadBoard();
            if (employee) await this.openInspector(employee);
        },
        async onInspectorAbsenceAdded() {
            this.historyRefreshToken += 1;
            await this.loadBoard();
        },
        cloneBoard() {
            return JSON.parse(JSON.stringify(this.board));
        },
        arraysWithLocations() {
            const locations = [
                {target: {type: 'home', id: null}, employees: this.lanes.home},
                {
                    target: {type: 'requires_distribution', id: null},
                    employees: this.lanes.requires_distribution
                },
                {target: {type: 'reserve', id: null}, employees: this.lanes.reserve}
            ];
            this.groups.forEach(group => {
                locations.push({
                    target: {type: 'object_group', id: group.id},
                    employees: group.employees
                });
                (Array.isArray(group.objects) ? group.objects : []).forEach(object => {
                    locations.push({
                        target: {type: 'object', id: this.objectId(object)},
                        employees: object.employees
                    });
                });
            });
            this.standaloneObjects.forEach(object => {
                locations.push({
                    target: {type: 'object', id: this.objectId(object)},
                    employees: object.employees
                });
            });
            return locations;
        },
        sameTarget(left, right) {
            return left && right &&
                left.type === right.type &&
                String(left.id || '') === String(right.id || '');
        },
        canUseTarget(target) {
            if (!target || !this.canMove) {
                return false;
            }
            if (target.type === 'home' || target.type === 'requires_distribution') {
                return this.canMoveAllObjects;
            }
            if (target.type === 'object_group') {
                const group = this.groups.find(item => String(item.id) === String(target.id));
                return Boolean(group && group.can_move === true);
            }
            if (target.type === 'object') {
                const object = this.groups.reduce((found, group) => {
                    if (found) {
                        return found;
                    }
                    return (Array.isArray(group.objects) ? group.objects : []).find(item => (
                        String(this.objectId(item)) === String(target.id)
                    )) || null;
                }, null) || this.standaloneObjects.find(item => (
                    String(this.objectId(item)) === String(target.id)
                ));
                return Boolean(object && object.can_move === true);
            }
            return false;
        },
        currentEmployeeTarget(employee) {
            return this.assignmentTarget(this.employeePrimaryAssignment(employee));
        },
        targetName(target) {
            if (!target) {
                return null;
            }
            if (target.type === 'home') {
                return 'Дома / отдых';
            }
            if (target.type === 'requires_distribution') {
                return 'Не распределён / выбыл из планирования';
            }
            if (target.type === 'object_group') {
                const group = this.groups.find(item => (
                    String(item.id) === String(target.id)
                ));
                return group ? group.name : null;
            }
            if (target.type === 'object') {
                const object = this.groups.reduce((found, group) => {
                    if (found) {
                        return found;
                    }
                    return (Array.isArray(group.objects) ? group.objects : []).find(item => (
                        String(this.objectId(item)) === String(target.id)
                    )) || null;
                }, null) || this.standaloneObjects.find(item => (
                    String(this.objectId(item)) === String(target.id)
                ));
                return object ? object.name : null;
            }
            return null;
        },
        applyOptimisticMove(employee, target) {
            const id = this.employeeId(employee);
            const primaryAssignment = this.employeePrimaryAssignment(employee);
            const destination = this.arraysWithLocations().find(location => (
                this.sameTarget(location.target, target)
            ));
            if (!destination || !Array.isArray(destination.employees)) {
                return false;
            }
            let movedEmployee = null;
            this.arraysWithLocations().forEach(location => {
                if (!Array.isArray(location.employees)) {
                    return;
                }
                for (let index = location.employees.length - 1; index >= 0; index -= 1) {
                    const candidate = location.employees[index];
                    if (String(this.employeeId(candidate)) !== String(id)) {
                        continue;
                    }
                    const assignment = this.employeeAssignment(candidate);
                    const samePrimary = primaryAssignment
                        ? this.sameAssignment(assignment, primaryAssignment)
                        : this.assignmentIsPrimary(assignment);
                    if (samePrimary) {
                        movedEmployee = candidate;
                        location.employees.splice(index, 1);
                        continue;
                    }
                    if (Array.isArray(candidate.visible_assignments)) {
                        candidate.visible_assignments =
                            candidate.visible_assignments.map(item => (
                                primaryAssignment &&
                                this.sameAssignment(item, primaryAssignment)
                                    ? this.assignmentWithTarget(item, target)
                                    : item
                            ));
                    }
                }
            });
            if (!movedEmployee) {
                return false;
            }
            const movedAssignment = this.assignmentWithTarget(
                this.employeeAssignment(movedEmployee) || primaryAssignment,
                target
            );
            const currentVisibleAssignments = Array.isArray(
                movedEmployee.visible_assignments
            )
                ? movedEmployee.visible_assignments
                : [];
            const movedVisibleAssignments = primaryAssignment
                ? currentVisibleAssignments.map(item => (
                    this.sameAssignment(item, primaryAssignment)
                        ? movedAssignment
                        : item
                ))
                : currentVisibleAssignments.filter(item => (
                    !this.assignmentIsPrimary(item)
                ));
            if (!movedVisibleAssignments.some(item => (
                this.sameAssignment(item, movedAssignment)
            ))) {
                movedVisibleAssignments.unshift(movedAssignment);
            }
            movedEmployee = Object.assign({}, movedEmployee, {
                assignment: movedAssignment,
                assignment_context: movedAssignment,
                assignment_warning: null,
                visible_assignments: movedVisibleAssignments
            });
            destination.employees.push(movedEmployee);
            return true;
        },
        assignmentWithTarget(assignment, target) {
            const source = assignment || {};
            const isObject = target && target.type === 'object';
            const isGroup = target && target.type === 'object_group';
            return Object.assign({}, source, {
                kind: 'primary',
                assignment_kind: 'primary',
                is_primary: true,
                target_type: target ? target.type : null,
                target_id: target ? target.id : null,
                target_name: this.targetName(target),
                building_id: isObject ? target.id : null,
                object_group_id: isGroup ? target.id : null
            });
        },
        async moveEmployee(employee, target, note = '', options = {}) {
            const returnFromReserve = options.returnFromReserve === true;
            const movementDate = /^\d{4}-\d{2}-\d{2}$/.test(
                String(options.effectiveDate || '')
            )
                ? String(options.effectiveDate)
                : this.boardDate;
            const isFutureMovement = movementDate > currentLocalDate();
            if (
                !this.movementAllowed ||
                (
                    !(
                        returnFromReserve
                            ? this.canReturnReserveEmployee(employee)
                            : this.canMoveEmployee(employee)
                    ) &&
                    options.allowTargetOnly !== true
                ) ||
                !this.canUseTarget(target)
            ) {
                return false;
            }
            if (isFutureMovement) {
                const employeeId = this.employeeId(employee);
                const query = {
                    date: movementDate,
                    employee_id: employeeId,
                    plan_intent: 'edit'
                };
                if (target.type === 'object') {
                    query.state_type = 'object';
                    query.building_id = target.id;
                } else if (target.type === 'requires_distribution') {
                    query.state_type = 'requires_distribution';
                }
                this.$bvToast.toast(
                    'Укажите период и явно сохраните изменение в плане',
                    {variant: 'info', solid: true, autoHideDelay: 3500}
                );
                this.$router.push({
                    name: 'employees-workforce-plan',
                    query
                });
                return true;
            }
            if (this.employeeHasAdditionalTarget(employee, target)) {
                const message = 'Этот объект уже является дополнительным назначением ' +
                    'сотрудника. Сначала снимите дополнительное назначение.';
                this.errorMessage = message;
                this.$bvToast.toast(message, {
                    variant: 'warning',
                    solid: true,
                    autoHideDelay: 4500
                });
                return false;
            }
            const currentTarget = this.currentEmployeeTarget(employee);
            if (this.sameTarget(currentTarget, target)) {
                this.$bvToast.toast('Сотрудник уже находится в выбранном месте', {
                    variant: 'info',
                    solid: true,
                    autoHideDelay: 2500
                });
                return false;
            }
            const boardRequestId = this.boardRequestId;
            const canApplyOptimistically = !isFutureMovement &&
                !returnFromReserve &&
                movementDate === this.boardDate;
            const snapshot = canApplyOptimistically
                ? this.cloneBoard()
                : null;
            this.clearUndoState();
            if (
                canApplyOptimistically &&
                !this.applyOptimisticMove(employee, target)
            ) {
                this.errorMessage = 'Не удалось определить место назначения на доске';
                return false;
            }
            this.refreshSelectedEmployee();
            this.movementPending = true;
            try {
                const employeeId = this.employeeId(employee);
                const payload = {
                    target_type: target.type,
                    target_id: target.id === null || target.id === '' ? null : target.id,
                    expected_state_token:
                        employee.workforce_state_token || null,
                    expected_state_date:
                        employee.workforce_state_date || this.boardDate
                };
                payload.effective_date = movementDate;
                if (note) {
                    payload.note = note;
                }
                if (returnFromReserve) {
                    payload.return_from_reserve = true;
                }
                const response = await api.post(
                    `/api/employees/${employeeId}/movements`,
                    payload,
                    {ignoreModal: true}
                );
                const data = response && response.data && response.data.data
                    ? response.data.data
                    : (response ? response.data : {});
                const eventId = data.event_id ||
                    data.id ||
                    (data.movement && (data.movement.event_id || data.movement.id));
                if (returnFromReserve) {
                    await this.loadBoard();
                    this.$bvToast.toast(
                        'Сотрудник возвращён в действующие и назначен',
                        {
                            variant: 'success',
                            solid: true,
                            autoHideDelay: 3500
                        }
                    );
                } else if (eventId) {
                    await this.loadBoard({preserveUndo: true});
                    this.startUndo(eventId, snapshot, data.undo_until);
                } else {
                    await this.loadBoard({preserveUndo: true});
                }
                this.highlightMovedEmployee(employeeId);
                return true;
            } catch (error) {
                const conflict = error && error.response &&
                    error.response.status === 409 &&
                    error.response.data &&
                    error.response.data.code === 'WORKFORCE_STATE_CONFLICT';
                if (conflict) {
                    await this.loadBoard({preserveUndo: true});
                } else if (
                    snapshot &&
                    this.boardDate === movementDate &&
                    this.boardRequestId === boardRequestId
                ) {
                    this.board = snapshot;
                    this.refreshSelectedEmployee();
                } else {
                    await this.loadBoard({preserveUndo: true});
                }
                this.errorMessage = this.errorText(error, 'Не удалось переместить сотрудника');
                return false;
            } finally {
                this.movementPending = false;
                this.$nextTick(this.flushQueuedRealtimeRefresh);
            }
        },
        async confirmPlannedMove(employee) {
            const plannedMove = employee && employee.planned_move;
            if (
                !plannedMove
                || !plannedMove.id
                || this.plannedMovePending
                || !this.canConfirmPlannedMove(employee)
            ) {
                return;
            }
            const destination = plannedMove.target_name || 'выбранное место';
            let confirmed = false;
            this.plannedMoveConfirmationVisible = true;
            await this.$nextTick();
            try {
                confirmed = await this.$bvModal.msgBoxConfirm(
                    `Подтвердить, что сотрудник «${this.employeeName(employee)}» ` +
                    `фактически перемещён в «${destination}»?`,
                    {
                        title: 'Подтверждение прибытия',
                        okTitle: 'Подтвердить прибытие',
                        cancelTitle: 'Не сейчас',
                        okVariant: 'success',
                        cancelVariant: 'outline-secondary',
                        centered: true
                    }
                );
            } finally {
                this.plannedMoveConfirmationVisible = false;
            }
            if (!confirmed) return;
            this.plannedMovePending = true;
            this.errorMessage = '';
            try {
                await api.post(
                    `/api/employees/planned-moves/${plannedMove.id}/confirm`,
                    {
                        expected_state_token:
                            employee.workforce_state_token || null,
                        expected_state_date:
                            employee.workforce_state_date || this.boardDate
                    },
                    {ignoreModal: true}
                );
                await this.loadBoard();
                this.highlightMovedEmployee(this.employeeId(employee));
                this.$bvToast.toast('Плановое перемещение подтверждено', {
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 3000
                });
            } catch (error) {
                const conflict = error && error.response &&
                    error.response.status === 409 &&
                    error.response.data &&
                    error.response.data.code === 'WORKFORCE_STATE_CONFLICT';
                if (conflict) await this.loadBoard();
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось подтвердить плановое перемещение'
                );
            } finally {
                this.plannedMovePending = false;
                this.$nextTick(this.flushQueuedRealtimeRefresh);
            }
        },
        async cancelPlannedMove(employee) {
            const plannedMove = employee && employee.planned_move;
            if (
                !plannedMove
                || !plannedMove.id
                || this.plannedMovePending
                || !this.canMoveEmployee(employee)
            ) {
                return;
            }
            let confirmed = false;
            this.plannedMoveConfirmationVisible = true;
            await this.$nextTick();
            try {
                confirmed = await this.$bvModal.msgBoxConfirm(
                    `Отменить плановое перемещение сотрудника «${this.employeeName(employee)}»?`,
                    {
                        title: 'Отмена планового перемещения',
                        okTitle: 'Отменить план',
                        cancelTitle: 'Оставить',
                        okVariant: 'danger',
                        cancelVariant: 'outline-secondary',
                        centered: true
                    }
                );
            } finally {
                this.plannedMoveConfirmationVisible = false;
            }
            if (!confirmed) return;
            this.plannedMovePending = true;
            this.errorMessage = '';
            try {
                await api.post(
                    `/api/employees/planned-moves/${plannedMove.id}/cancel`,
                    {reason: 'Отменено с доски распределения'},
                    {ignoreModal: true}
                );
                await this.loadBoard();
                this.$bvToast.toast('Плановое перемещение отменено', {
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 3000
                });
            } catch (error) {
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось отменить плановое перемещение'
                );
            } finally {
                this.plannedMovePending = false;
                this.$nextTick(this.flushQueuedRealtimeRefresh);
            }
        },
        startUndo(eventId, snapshot, undoUntil) {
            this.clearUndoState();
            const parsedUndoUntil = undoUntil ? new Date(undoUntil).getTime() : NaN;
            const expiresAt = Number.isFinite(parsedUndoUntil) && parsedUndoUntil > Date.now()
                ? parsedUndoUntil
                : Date.now() + 10000;
            this.undoState = {
                eventId,
                snapshot,
                expiresAt
            };
            this.updateUndoSeconds();
            this.undoTimer = window.setInterval(this.updateUndoSeconds, 250);
        },
        updateUndoSeconds() {
            if (!this.undoState) {
                this.undoSeconds = 0;
                return;
            }
            const remaining = this.undoState.expiresAt - Date.now();
            if (remaining <= 0) {
                this.clearUndoState();
                this.loadBoard();
                return;
            }
            this.undoSeconds = Math.ceil(remaining / 1000);
        },
        clearUndoState() {
            this.stopUndoTimer();
            this.undoState = null;
            this.undoSeconds = 0;
        },
        stopUndoTimer() {
            if (this.undoTimer) {
                window.clearInterval(this.undoTimer);
                this.undoTimer = null;
            }
        },
        async undoMovement() {
            if (!this.undoState || this.undoPending) {
                return;
            }
            const state = this.undoState;
            this.undoPending = true;
            this.stopUndoTimer();
            try {
                await api.post(
                    `/api/employees/movements/${state.eventId}/undo`,
                    {},
                    {ignoreModal: true}
                );
                if (
                    this.undoState &&
                    String(this.undoState.eventId) === String(state.eventId)
                ) {
                    this.clearUndoState();
                }
                await this.loadBoard({preserveUndo: true});
                this.$bvToast.toast('Перемещение отменено', {
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 2500
                });
            } catch (error) {
                this.errorMessage = this.errorText(error, 'Не удалось отменить перемещение');
                const canRetry = this.undoState &&
                    String(this.undoState.eventId) === String(state.eventId) &&
                    this.undoState.expiresAt > Date.now();
                if (canRetry) {
                    this.updateUndoSeconds();
                    this.undoTimer = window.setInterval(this.updateUndoSeconds, 250);
                } else {
                    this.clearUndoState();
                    this.loadBoard({preserveUndo: true});
                }
            } finally {
                this.undoPending = false;
                this.$nextTick(this.flushQueuedRealtimeRefresh);
            }
        }
    }
};
</script>

<style scoped>
.workforce-page {
    min-width: 0;
    padding: 16px;
    color: #2d3e50;
}

.workforce-sync-state {
    min-height: 24px;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    color: #66788a;
    font-size: 12px;
}

.workforce-sync-state__connection,
.workforce-sync-state__pending,
.workforce-sync-state__notice {
    display: inline-flex;
    align-items: center;
    gap: 5px;
}

.workforce-sync-state__connection i {
    font-size: 7px;
}

.workforce-sync-state__connection--connected i {
    color: #28a745;
}

.workforce-sync-state__connection--connecting i,
.workforce-sync-state__connection--reconnecting i {
    color: #f0ad4e;
}

.workforce-sync-state__connection--offline i {
    color: #dc3545;
}

.workforce-sync-state__pending {
    color: #9a6700;
}

.workforce-sync-state__notice {
    color: #287d48;
}

.workforce-page__header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 14px;
}

.workforce-page__title {
    min-width: 0;
}

.workforce-page__title-row {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 6px 14px;
}

.workforce-page__header h1 {
    margin: 0;
    color: #283a4d;
    font-size: 1.45rem;
    font-weight: 700;
}

.workforce-page__header p {
    margin: 3px 0 0;
    color: #748291;
    font-size: 0.84rem;
}

.workforce-page__space-control {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    flex: 0 0 auto;
}

.workforce-create-employee-slot {
    display: inline-flex;
    width: 11.5rem;
    min-height: 31px;
    flex: 0 0 11.5rem;
}

.workforce-plan-slice-notice {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
    font-size: 0.8rem;
}

.workforce-page__space-hint {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #697c8e;
    font-size: 0.73rem;
    font-weight: 600;
    white-space: nowrap;
}

.workforce-page__space-hint i {
    color: #d19024;
}

.workforce-page__space-toggle {
    min-width: 132px;
    font-weight: 700;
    white-space: nowrap;
    box-shadow: 0 3px 9px rgba(36, 104, 168, 0.18);
}

.workforce-overview-controls {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-bottom: 8px;
    padding: 6px;
    border: 1px solid #c7d5e2;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.97);
    box-shadow: 0 2px 8px rgba(34, 55, 75, 0.08);
    backdrop-filter: blur(10px);
}

.workforce-overview-controls--overview {
    position: sticky;
    z-index: 1025;
    top: calc(var(--shell-header-height, 64px) + 5px);
}

.workforce-overview-controls__switch {
    flex: 0 0 auto;
    box-shadow: 0 2px 6px rgba(23, 111, 242, 0.16);
}

.workforce-overview-controls__switch .btn {
    min-width: 104px;
    font-weight: 800;
}

.workforce-overview-controls__search {
    min-width: 190px;
    max-width: 620px;
    flex: 1 1 360px;
}

.workforce-overview-controls__citizenship {
    width: 175px;
    flex: 0 0 175px;
}

.workforce-overview-controls__filters {
    display: inline-flex;
    min-width: 31px;
    height: 30px;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 0 7px;
    border-radius: 999px;
    background: #edf1f5;
    color: #6d7c8c;
    font-size: 0.72rem;
    font-weight: 800;
}

.workforce-overview-controls__filters--active {
    background: #fff0cf;
    color: #875c00;
}

.workforce-toolbar__date {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
    padding: 5px 6px 5px 8px;
    border: 1px solid #dce3ea;
    border-radius: 7px;
    background: #f8fafc;
}

.workforce-toolbar__date--filtered {
    border-color: #e0ad54;
    background: #fff8e8;
    box-shadow: inset 3px 0 0 #e0a13a;
}

.workforce-toolbar__date label {
    margin: 0;
    color: #53687c;
    font-size: 0.73rem;
    font-weight: 700;
    white-space: nowrap;
}

.workforce-toolbar__date .form-control {
    width: 132px;
}

.workforce-toolbar__today {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-weight: 700;
    white-space: nowrap;
}

.workforce-toolbar {
    position: sticky;
    z-index: 1015;
    top: calc(var(--shell-header-height, 64px) + 8px);
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 13px;
    padding: 10px;
    border: 1px solid #dce3ea;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 5px 14px rgba(31, 52, 73, 0.08);
}

.workforce-toolbar--filtered {
    border-color: #d8ad68;
    box-shadow: 0 5px 16px rgba(130, 91, 26, 0.13);
}

.workforce-toolbar--collapsed {
    justify-content: flex-start;
    gap: 6px;
    min-height: 38px;
    padding: 5px 8px;
}

.workforce-toolbar__desktop-collapse {
    position: absolute;
    z-index: 1;
    top: 8px;
    right: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 32px;
    padding: 0;
}

.workforce-toolbar__collapsed-strip {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
    color: #7b8895;
    font-size: 0.72rem;
}

.workforce-toolbar__collapsed-strip button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border: 1px solid #b7c9da;
    border-radius: 6px;
    background: #f6f9fc;
    color: #426986;
    font-size: 0.74rem;
    font-weight: 700;
}

.workforce-toolbar__collapsed-strip button:hover,
.workforce-toolbar__collapsed-strip button:focus {
    border-color: #7fa8cf;
    background: #eaf3fb;
    color: #2468a8;
}

.workforce-toolbar__collapsed-date,
.workforce-toolbar__collapsed-filter-count {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-height: 26px;
    padding: 4px 8px;
    border-radius: 6px;
    background: #f2f5f8;
    color: #52687c;
    font-weight: 700;
    white-space: nowrap;
}

.workforce-toolbar__collapsed-date i {
    color: #6584a3;
}

.workforce-toolbar__collapsed-filter-count {
    min-width: 31px;
    justify-content: center;
}

.workforce-toolbar__collapsed-filter-count--active {
    background: #e8f2fc;
    color: #2468a8;
}

@media (min-width: 768px) {
    .workforce-toolbar:not(.workforce-toolbar--collapsed) {
        padding-right: 52px;
    }
}

.workforce-toolbar--collapsed .workforce-active-filters {
    flex: 1 1 auto;
    order: 0;
    min-width: 0;
    padding: 0;
    border: 0;
    background: transparent;
}

.workforce-toolbar__filters,
.workforce-toolbar__summary {
    display: flex;
    align-items: center;
    gap: 8px;
}

.workforce-toolbar__filters {
    flex: 1 1 auto;
    flex-wrap: wrap;
    min-width: 0;
}

.workforce-toolbar__search {
    width: min(390px, 45%);
    min-width: 240px;
}

.workforce-toolbar__select {
    width: 190px;
}

.workforce-toolbar__priority {
    width: 165px;
}

.workforce-active-filters {
    display: flex;
    align-items: center;
    flex: 1 0 100%;
    flex-wrap: wrap;
    gap: 0.35rem;
    order: 3;
    padding: 0.4rem 0.5rem;
    border: 1px solid #ffe08a;
    border-radius: 0.35rem;
    background: #fff8e1;
    color: #5c4300;
    font-size: 0.84rem;
}

.workforce-active-filters__label {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    flex: 0 0 auto;
    font-weight: 700;
}

.workforce-active-filter {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    max-width: 100%;
    min-height: 28px;
    padding: 0.2rem 0.45rem;
    border: 1px solid transparent;
    border-radius: 999px;
    color: #fff;
    font-size: 0.8rem;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
}

.workforce-active-filter:hover:not(:disabled),
.workforce-active-filter:focus:not(:disabled) {
    filter: brightness(0.94);
}

.workforce-active-filter:disabled,
.workforce-active-filters__reset:disabled {
    cursor: wait;
    opacity: 0.65;
}

.workforce-active-filter__text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workforce-active-filter__remove {
    margin-left: 0.1rem;
    opacity: 0.85;
}

.workforce-active-filter--primary {
    border-color: #007bff;
    background: #007bff;
}

.workforce-active-filter--secondary {
    border-color: #6c757d;
    background: #6c757d;
}

.workforce-active-filter--warning {
    border-color: #ffc107;
    background: #ffc107;
    color: #212529;
}

.workforce-active-filter--info {
    border-color: #17a2b8;
    background: #17a2b8;
}

.workforce-active-filter--success {
    border-color: #218838;
    background: #218838;
}

.workforce-active-filters__reset {
    flex: 0 0 auto;
    min-height: 28px;
    padding: 0.2rem 0.5rem;
    border: 1px solid #b38300;
    border-radius: 0.25rem;
    background: #fff;
    color: #5c4300;
    font-size: 0.8rem;
    font-weight: 700;
    line-height: 1.2;
}

.workforce-active-filters__reset:hover:not(:disabled),
.workforce-active-filters__reset:focus:not(:disabled) {
    background: #fff3bf;
}

.workforce-toolbar__summary {
    flex: 0 0 auto;
    flex-wrap: wrap;
    justify-content: flex-end;
    color: #5e6f80;
    font-size: 0.78rem;
}

.workforce-toolbar__summary > span {
    padding: 5px 8px;
    border-radius: 6px;
    background: #f2f5f8;
    white-space: nowrap;
}

.workforce-toolbar__summary i {
    margin-right: 4px;
    color: #6584a3;
}

.workforce-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    min-height: 280px;
    padding: 30px;
    border: 1px dashed #d3dce5;
    border-radius: 12px;
    background: #fafbfd;
    color: #6f7d8b;
}

.workforce-state--empty {
    flex-direction: column;
    text-align: center;
}

.workforce-state--empty > i {
    margin-bottom: 4px;
    color: #a0adba;
    font-size: 2rem;
}

.workforce-state--empty strong {
    color: #536272;
}

.workforce-state--empty span {
    font-size: 0.82rem;
}

.workforce-empty-notice {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
    padding: 10px 12px;
    border: 1px solid #cdddea;
    border-radius: 9px;
    background: #f2f7fb;
    color: #526b81;
}

.workforce-empty-notice > i {
    color: #6f91ae;
    font-size: 1.1rem;
}

.workforce-empty-notice > div {
    display: flex;
    flex-direction: column;
}

.workforce-empty-notice strong {
    font-size: 0.82rem;
}

.workforce-empty-notice span {
    margin-top: 1px;
    font-size: 0.73rem;
}

.workforce-context-menu {
    position: fixed;
    z-index: 1055;
    width: 220px;
    overflow: hidden;
    padding: 5px;
    border: 1px solid #d7dee6;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 10px 28px rgba(27, 39, 52, 0.2);
}

.workforce-context-menu button {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    padding: 8px 9px;
    border: 0;
    border-radius: 5px;
    background: transparent;
    color: #35485b;
    font-size: 0.8rem;
    text-align: left;
}

.workforce-context-menu button:hover,
.workforce-context-menu button:focus {
    background: #edf4fb;
    color: #225e99;
}

.workforce-context-menu button i {
    width: 16px;
    color: #6c8298;
    text-align: center;
}

.workforce-undo {
    position: fixed;
    z-index: 1070;
    right: 22px;
    bottom: 22px;
    display: flex;
    align-items: center;
    gap: 22px;
    min-width: 330px;
    padding: 12px 14px;
    border-radius: 9px;
    background: #263746;
    box-shadow: 0 9px 25px rgba(27, 38, 48, 0.25);
    color: #fff;
}

.workforce-undo div {
    display: flex;
    flex-direction: column;
}

.workforce-undo strong {
    font-size: 0.84rem;
}

.workforce-undo span {
    color: #cbd5dd;
    font-size: 0.72rem;
}

.workforce-undo button {
    padding: 5px 9px;
    border: 1px solid #7fb4e8;
    border-radius: 5px;
    background: transparent;
    color: #9fcbf5;
    font-size: 0.78rem;
    font-weight: 700;
}

.workforce-undo button:disabled {
    opacity: 0.65;
}

@media (max-width: 991.98px) {
    .workforce-toolbar {
        align-items: stretch;
        flex-direction: column;
    }

    .workforce-toolbar__summary {
        justify-content: flex-end;
    }
}

@media (max-width: 767.98px) {
    .workforce-page {
        padding: 10px;
    }

    .workforce-page__header {
        align-items: stretch;
        flex-direction: column;
        gap: 10px;
    }

    .workforce-page__title-row {
        gap: 4px 10px;
    }

    .workforce-page--overview {
        padding: 6px;
    }

    .workforce-page--overview .workforce-page__header {
        display: none;
    }

    .workforce-overview-controls {
        flex-wrap: wrap;
        gap: 4px;
        margin-bottom: 5px;
        padding: 4px;
    }

    .workforce-overview-controls--overview {
        top: calc(var(--shell-header-height, 56px) + 3px);
    }

    .workforce-overview-controls .btn {
        min-height: 30px;
        padding: 4px 7px;
        font-size: 0.68rem;
    }

    .workforce-overview-controls__switch {
        flex: 1 1 auto;
    }

    .workforce-overview-controls__switch .btn {
        min-width: 0;
        flex: 1 1 50%;
    }

    .workforce-overview-controls__search {
        order: 3;
        width: 100%;
        max-width: none;
        flex-basis: 100%;
    }

    .workforce-overview-controls__citizenship,
    .workforce-overview-controls__qualifications {
        order: 4;
        width: 100%;
        flex-basis: 100%;
    }

    .workforce-toolbar {
        top: calc(var(--shell-header-height, 56px) + 5px);
        gap: 5px;
        margin-bottom: 8px;
        padding: 6px;
        border-radius: 8px;
        touch-action: pan-y;
    }

    .workforce-toolbar input,
    .workforce-toolbar select,
    .workforce-toolbar button,
    .workforce-active-filter,
    .workforce-active-filters__reset {
        touch-action: pan-y;
    }

    .workforce-toolbar__date {
        position: relative;
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        grid-column: 1 / -1;
        gap: 4px;
        padding: 2px 3px;
    }

    .workforce-toolbar__date label {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        clip-path: inset(50%);
        white-space: nowrap;
    }

    .workforce-toolbar__date .form-control {
        width: 100%;
    }

    .workforce-toolbar__filters {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 5px;
        width: 100%;
    }

    .workforce-toolbar__search {
        grid-column: 1 / -1;
    }

    .workforce-toolbar__mobile-toggle {
        display: flex;
        align-items: center;
        justify-content: space-between;
        grid-column: 1 / -1;
        padding: 0.25rem 0.55rem;
        font-size: 0.76rem;
        font-weight: 700;
        text-align: left;
    }

    .workforce-toolbar__mobile-toggle > span {
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }

    .workforce-toolbar__search,
    .workforce-toolbar__select,
    .workforce-toolbar__priority {
        width: 100%;
        min-width: 0;
    }

    .workforce-toolbar__summary {
        width: 100%;
        flex-wrap: wrap;
        justify-content: flex-start;
        gap: 4px;
        padding-bottom: 1px;
    }

    .workforce-toolbar__summary > span {
        flex: 0 0 auto;
        padding: 4px 6px;
        font-size: 0.72rem;
    }

    .workforce-toolbar__summary .btn,
    .workforce-toolbar__today {
        flex: 0 0 auto;
        padding: 0.2rem 0.45rem;
        font-size: 0.75rem;
        white-space: nowrap;
    }

    .workforce-active-filters {
        width: 100%;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.25rem;
        padding: 0.22rem 0.3rem;
    }

    .workforce-active-filters__label {
        flex: 1 0 100%;
        font-size: 0.72rem;
    }

    .workforce-active-filters__reset {
        width: auto;
        min-height: 25px;
        padding: 0.15rem 0.4rem;
        font-size: 0.72rem;
        white-space: nowrap;
    }

    .workforce-active-filter {
        max-width: min(78vw, 300px);
        min-height: 25px;
        padding: 0.15rem 0.4rem;
        font-size: 0.72rem;
        white-space: nowrap;
    }

    .workforce-undo {
        right: 10px;
        bottom: 76px;
        left: 10px;
        min-width: 0;
    }
}
</style>

<style>
.workforce-overview-hint {
    max-width: min(320px, calc(100vw - 24px));
}

.workforce-overview-hint .popover-body {
    color: #3f5367;
    font-size: 0.78rem;
}

.workforce-overview-hint .popover-body p {
    margin-bottom: 9px;
}

body.employee-workforce-overview-landscape .modern-shell {
    --shell-header-height: 0px;
    --shell-sidebar-width: 0px;
}

body.employee-workforce-overview-landscape .modern-shell__header,
body.employee-workforce-overview-landscape .modern-shell__bottom-nav,
body.employee-workforce-overview-landscape .modern-shell__sidebar,
body.employee-workforce-overview-landscape
.workforce-page--overview .workforce-page__header {
    display: none !important;
}

body.employee-workforce-overview-landscape .modern-shell__content {
    height: 100vh;
    height: 100dvh;
    min-height: 0;
    overflow: hidden;
    padding-top: 0 !important;
    padding-right: 4px !important;
    padding-right: max(4px, env(safe-area-inset-right)) !important;
    padding-bottom: 0 !important;
    padding-left: 4px !important;
    padding-left: max(4px, env(safe-area-inset-left)) !important;
}

body.employee-workforce-overview-landscape .workforce-page--overview {
    box-sizing: border-box;
    display: flex;
    height: 100%;
    min-height: 0;
    flex-direction: column;
    padding-top: 4px;
    padding-bottom: calc(4px + env(safe-area-inset-bottom));
}

body.employee-workforce-overview-landscape .workforce-overview-controls {
    flex: 0 0 auto;
    top: 0;
}

body.employee-workforce-overview-landscape .workforce-page--overview >
.workforce-grid-shell {
    min-height: 0;
    flex: 1 1 auto;
}

body.employee-workforce-overview-landscape .workforce-overview {
    height: 100%;
    min-height: 0;
}
</style>
